import React from 'react';
import { caseApi, deviceApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  CustomerFacingSlaReportView,
  normalizeCustomerFacingSlaReport
} from './CustomerFacingSlaReportView';
import { CaseAttachmentViewerModal } from './CaseAttachmentViewerModal';
import {
  getCurrentDeviceSlaAssignment,
  getDeviceSlaLabel,
  getDeviceSlaPeriodLabel,
  hasSavedDeviceSlaReport
} from '../utils/deviceSla';
import {
  getDeviceWarrantyChipLabel,
  getDeviceWarrantyChipStyle,
  getDeviceWarrantyFirstMonthChipStyle,
  getDeviceWarrantyStatus
} from '../utils/deviceWarranty';
import { mergeCaseTaskUpdate } from '../utils/caseTaskMerge';
import type { CaseSurveyResponseDetail, CaseSurveyResponseSection } from '../types';

const CASE_BRAND_TEXT = '#123F43';
const CASE_BRAND_MUTED = '#64716F';
const CASE_BRAND_BORDER = '#DDD4C8';
const CASE_BRAND_CARD = '#FFFDF9';
const CASE_BRAND_PANEL = '#FBF8F2';
const CASE_BRAND_TEXT_TINT = 'rgba(18, 63, 67, 0.08)';
const CASE_BRAND_TEXT_TINT_BORDER = 'rgba(18, 63, 67, 0.18)';
const CASE_BRAND_ACCENT_BG = '#F6E7C9';
const CASE_BRAND_ACCENT_TEXT = '#8A611E';
const CASE_BRAND_SUCCESS_BG = '#E7F2EC';
const CASE_BRAND_SUCCESS_BORDER = '#BDD4C7';
const CASE_BRAND_SUCCESS_TEXT = '#25624D';

const getCaseSurveyScoreDisplay = (survey: any) => {
  if (survey?.scoreEarned === null || survey?.scoreEarned === undefined) return null;
  if (survey?.scorePossible === null || survey?.scorePossible === undefined) return null;
  const earned = Number(survey?.scoreEarned);
  const possible = Number(survey?.scorePossible);
  if (!Number.isFinite(earned) || !Number.isFinite(possible) || possible <= 0) return null;
  const percentage = Math.max(0, Math.min(100, Math.floor((earned / possible) * 100)));
  const tone =
    percentage <= 69
      ? { color: '#B91C1C', backgroundColor: '#FEF2F2', borderColor: '#FECACA' }
      : percentage <= 84
      ? { color: '#92400E', backgroundColor: '#FEF3C7', borderColor: '#FCD34D' }
      : percentage <= 94
      ? { color: CASE_BRAND_SUCCESS_TEXT, backgroundColor: CASE_BRAND_SUCCESS_BG, borderColor: CASE_BRAND_SUCCESS_BORDER }
      : { color: '#14532D', backgroundColor: '#DCFCE7', borderColor: '#86EFAC' };
  return {
    earned,
    possible,
    percentage,
    scoreLabel: `${earned}/${possible}`,
    percentageLabel: `${percentage}%`,
    tone
  };
};

const getCaseSurveyScaleLabel = (section: CaseSurveyResponseSection) => {
  const scaleType = String(section?.scaleType || '').trim().toUpperCase();
  if (scaleType === 'STARS_5') return '1-5 stars';
  if (scaleType === 'STARS_10') return '1-10 stars';
  if (scaleType === 'ZERO_TO_HUNDRED') return '0-100 score';
  return '0-10 score';
};

const parseCaseScheduleDate = (value?: string | null) => {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const formatCaseScheduleDateTime = (value: Date | null) =>
  value
    ? value.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    : 'Not set';

const formatCaseScheduleDuration = (startAt: Date | null, endAt: Date | null) => {
  if (!startAt || !endAt || endAt.getTime() <= startAt.getTime()) return 'Not set';
  const totalMinutes = Math.round((endAt.getTime() - startAt.getTime()) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.length ? parts.join(' ') : '0m';
};

const toCaseDateInputValue = (value: Date | null) =>
  value
    ? `${value.getFullYear()}-${`${value.getMonth() + 1}`.padStart(2, '0')}-${`${value.getDate()}`.padStart(2, '0')}`
    : '';

const toCaseTimeInputValue = (value: Date | null) =>
  value ? `${`${value.getHours()}`.padStart(2, '0')}:${`${value.getMinutes()}`.padStart(2, '0')}` : '';

const combineCaseDateTimeInput = (dateValue: string, timeValue: string) => {
  if (!dateValue || !timeValue) return null;
  const combined = new Date(`${dateValue}T${timeValue}`);
  return Number.isNaN(combined.getTime()) ? null : combined;
};

const formatCaseMediaFileSize = (value: any) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const resolveCaseMediaUrl = (baseUrl: string, fileUrl: string | null | undefined) => {
  const raw = String(fileUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(baseUrl || '').replace(/\/$/, '');
  if (!base) return raw;
  return `${base}${raw.startsWith('/') ? raw : `/${raw}`}`;
};

const DEFAULT_SCHEDULE_REQUEST_DURATION_MINUTES = 8 * 60;

const addCaseMinutes = (value: Date, minutes: number) =>
  new Date(value.getTime() + minutes * 60000);

type ScheduleRequestDraft = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  validationMessage: string | null;
};

const buildScheduleRequestDraft = (primaryWindowStart: Date | null, primaryWindowEnd: Date | null): ScheduleRequestDraft => {
  const fallbackStart = primaryWindowStart || new Date();
  const normalizedStart = new Date(fallbackStart);
  normalizedStart.setSeconds(0, 0);
  if (!primaryWindowStart) {
    normalizedStart.setHours(8, 0, 0, 0);
  }
  const primaryEnd =
    primaryWindowEnd && primaryWindowEnd.getTime() > normalizedStart.getTime()
      ? new Date(primaryWindowEnd)
      : addCaseMinutes(normalizedStart, DEFAULT_SCHEDULE_REQUEST_DURATION_MINUTES);
  const normalizedEnd = new Date(primaryEnd);
  normalizedEnd.setSeconds(0, 0);
  return {
    startDate: toCaseDateInputValue(normalizedStart),
    startTime: toCaseTimeInputValue(normalizedStart),
    endDate: toCaseDateInputValue(normalizedEnd),
    endTime: toCaseTimeInputValue(normalizedEnd),
    validationMessage: null
  };
};

const buildScheduleRequestDraftRange = (draft: ScheduleRequestDraft | null) => {
  const startAt = combineCaseDateTimeInput(draft?.startDate || '', draft?.startTime || '');
  const endAt = combineCaseDateTimeInput(draft?.endDate || '', draft?.endTime || '');
  return { startAt, endAt };
};

const getScheduleRequestDurationMinutes = (draft: ScheduleRequestDraft | null) => {
  const { startAt, endAt } = buildScheduleRequestDraftRange(draft);
  if (!startAt || !endAt) return DEFAULT_SCHEDULE_REQUEST_DURATION_MINUTES;
  const durationMinutes = Math.round((endAt.getTime() - startAt.getTime()) / 60000);
  return durationMinutes > 0 ? durationMinutes : DEFAULT_SCHEDULE_REQUEST_DURATION_MINUTES;
};

const alignScheduleRequestEndToStart = (
  nextDraft: ScheduleRequestDraft,
  durationMinutes = DEFAULT_SCHEDULE_REQUEST_DURATION_MINUTES
): ScheduleRequestDraft => {
  const { startAt } = buildScheduleRequestDraftRange(nextDraft);
  if (!startAt) {
    return nextDraft;
  }
  const safeDurationMinutes = durationMinutes > 0 ? durationMinutes : DEFAULT_SCHEDULE_REQUEST_DURATION_MINUTES;
  const nextEnd = addCaseMinutes(startAt, safeDurationMinutes);
  return {
    ...nextDraft,
    endDate: toCaseDateInputValue(nextEnd),
    endTime: toCaseTimeInputValue(nextEnd)
  };
};

export const TechDashboardCaseQuickView = React.memo(function TechDashboardCaseQuickView(props: any) {
  const {
    styles,
    caseItem,
    baseUrl,
    openedDate,
    analysisMeta,
    badgeStyle,
    workflowDraft,
    workflowDirty,
    workflowSaving,
    workflowClosing,
    workflowResolving,
    workflowError,
    currentTask,
    caseAssigneesLoading,
    caseAssigneeOptions,
    caseAssigneesError,
    canUpdateCaseStatus,
    canCloseCases,
    canUpdateCasePriority,
    canAssignCases,
    saveCaseWorkflow,
    closeCaseWorkflow,
    resolveCaseWorkflow,
    resetCaseWorkflowDraft,
    stageCaseWorkflowDraft,
    getCaseDisplayNumber,
    getStatusStyle,
    formatCaseStatusLabel,
    getPriorityStyle,
    formatTaskStatus,
    formatPriorityLabel,
    caseCardQuotes,
    caseCardQuotesLoading,
    setCaseQuotePreview,
    setCaseQuotePreviewContext,
    caseServiceReports,
    caseSurveys,
    caseServiceReportPreviewLoadingId,
    openCaseServiceReportPreview,
    getQuoteDecisionBadge,
    getQuotePartsBadge,
    isEditingProblem,
    canEditCaseProblem,
    startProblemEdit,
    problemParsed,
    editProblemSummary,
    setEditProblemSummary,
    isSavingProblem,
    editProblemDetails,
    updateProblemDetail,
    removeProblemDetailLine,
    addProblemDetailLine,
    problemEditError,
    saveProblemDetails,
    cancelProblemEdit,
    assignmentDescription,
    assignmentStatusParts,
    activeMeta,
    assignmentErrorMessage,
    activeGroupCode,
    INSTALL_PHASES,
    handleOpenInstallPhase,
    MAINTENANCE_VARIANTS,
    taskTypeCodeRaw,
    MAINTENANCE_VARIANT_ROUTES,
    navigate,
    handleAssignTaskGroup,
    MAINTENANCE_COLOR,
    casePath,
    disableCaseNavigation,
    reviewLabel,
    handleOpenDecommission,
    guardTaskWorkspaceOpen,
    TASK_GROUPS,
    isAssigning,
    assigningCaseId,
    assignmentError,
    setAssignmentError,
    applyCaseQuickViewRefresh,
    loadCaseDetails,
    closeCaseQuickView,
    getPendingDeviceInfo,
    canVerifySubmittedDevices,
    verifySubmittedDevice,
    devices
  } = props;
  const { user } = useAuth();
  const viewerRoleKey = String((user as any)?.role || '').trim().toUpperCase();
  const canViewSurveyResponseDetails = ['ADMIN', 'SUPERUSER'].includes(viewerRoleKey);

  const [workflowEditorOpen, setWorkflowEditorOpen] = React.useState(false);
  const pendingDeviceInfo = React.useMemo(() => {
    if (typeof getPendingDeviceInfo === 'function') {
      return getPendingDeviceInfo(caseItem);
    }
    const symptoms = Array.isArray(caseItem?.symptoms) ? caseItem.symptoms : [];
    return symptoms.find((symptom: any) =>
      symptom &&
      symptom.type === 'NEW_DEVICE' &&
      symptom.needsVerification !== false &&
      symptom.verified !== true
    ) || null;
  }, [caseItem, getPendingDeviceInfo]);
  const deviceVerificationRequired = Boolean(pendingDeviceInfo);
  const [verificationMode, setVerificationMode] = React.useState<'create' | 'link'>('create');
  const [verificationDraft, setVerificationDraft] = React.useState({
    modelNumber: '',
    manufacturer: '',
    serialNumber: '',
    assetNumber: '',
    deviceId: '',
    locationId: ''
  });
  const [verificationSaving, setVerificationSaving] = React.useState(false);
  const [verificationError, setVerificationError] = React.useState('');
  const [verificationInventory, setVerificationInventory] = React.useState<any[]>([]);
  const [verificationInventoryLoading, setVerificationInventoryLoading] = React.useState(false);
  const [verificationInventoryError, setVerificationInventoryError] = React.useState('');
  const [customerDeviceReviewOpen, setCustomerDeviceReviewOpen] = React.useState(false);
  const [verificationChecks, setVerificationChecks] = React.useState({
    identifiers: false,
    customer: false,
    model: false
  });
  const serviceReports = Array.isArray(caseServiceReports) ? caseServiceReports : [];
  const surveyRecords = Array.isArray(caseSurveys) ? caseSurveys : [];
  const [surveyResponseDetail, setSurveyResponseDetail] = React.useState<CaseSurveyResponseDetail | null>(null);
  const [surveyResponseLoadingId, setSurveyResponseLoadingId] = React.useState<string | null>(null);
  const [surveyResponseError, setSurveyResponseError] = React.useState('');
  const caseMedia = React.useMemo(
    () => (Array.isArray(caseItem?.media) ? caseItem.media.filter(Boolean) : []),
    [caseItem?.media]
  );
  const [caseAttachmentViewer, setCaseAttachmentViewer] = React.useState<any | null>(null);
  const activeSlaAssignment = React.useMemo(
    () =>
      getCurrentDeviceSlaAssignment(
        caseItem?.device?.slaAssignments,
        caseItem?.openedAt || caseItem?.createdAt || null
      ),
    [caseItem?.createdAt, caseItem?.device?.slaAssignments, caseItem?.openedAt]
  );
  const activeWarrantyStatus = React.useMemo(
    () => getDeviceWarrantyStatus(caseItem?.device, caseItem?.openedAt || caseItem?.createdAt || null),
    [caseItem?.createdAt, caseItem?.device?.warrantyEndAt, caseItem?.device?.warrantyStartAt, caseItem?.openedAt]
  );
  const activeSlaLabel = React.useMemo(
    () => getDeviceSlaLabel(activeSlaAssignment),
    [activeSlaAssignment]
  );
  const activeSlaPeriod = React.useMemo(
    () => getDeviceSlaPeriodLabel(activeSlaAssignment),
    [activeSlaAssignment]
  );
  const activeSlaHasSavedReport = React.useMemo(
    () => hasSavedDeviceSlaReport(activeSlaAssignment),
    [activeSlaAssignment]
  );
  const [deviceSlaReport, setDeviceSlaReport] = React.useState<any | null>(null);
  const [deviceSlaReportLoading, setDeviceSlaReportLoading] = React.useState(false);
  const [deviceSlaReportError, setDeviceSlaReportError] = React.useState('');
  const [slaAgreementViewerOpen, setSlaAgreementViewerOpen] = React.useState(false);
  const [scheduleRequestSaving, setScheduleRequestSaving] = React.useState(false);
  const [scheduleConfirmSaving, setScheduleConfirmSaving] = React.useState(false);
  const [scheduleRequestError, setScheduleRequestError] = React.useState('');
  const [scheduleRequestModalOpen, setScheduleRequestModalOpen] = React.useState(false);
  const [scheduleRequestDraft, setScheduleRequestDraft] = React.useState<ScheduleRequestDraft | null>(null);

  React.useEffect(() => {
    setWorkflowEditorOpen(false);
    setSlaAgreementViewerOpen(false);
    setScheduleRequestSaving(false);
    setScheduleConfirmSaving(false);
    setScheduleRequestError('');
    setScheduleRequestModalOpen(false);
    setScheduleRequestDraft(null);
    setCaseAttachmentViewer(null);
    setSurveyResponseDetail(null);
    setSurveyResponseLoadingId(null);
    setSurveyResponseError('');
    setVerificationMode('create');
    setVerificationError('');
    setVerificationInventoryError('');
    setCustomerDeviceReviewOpen(false);
    setVerificationChecks({
      identifiers: false,
      customer: false,
      model: false
    });
  }, [caseItem?.id]);

  React.useEffect(() => {
    setVerificationDraft({
      modelNumber:
        String(pendingDeviceInfo?.modelNumber || caseItem?.device?.modelNumber || 'Other').trim() || 'Other',
      manufacturer:
        String(pendingDeviceInfo?.manufacturer || caseItem?.device?.manufacturer || '').trim(),
      serialNumber:
        String(
          pendingDeviceInfo?.serialNumber ||
            (pendingDeviceInfo?.temporarySerialNumber ? '' : caseItem?.device?.serialNumber) ||
            ''
        ).trim(),
      assetNumber:
        String(pendingDeviceInfo?.assetNumber || (caseItem?.device as any)?.assetNumber || '').trim(),
      deviceId: '',
      locationId:
        String(pendingDeviceInfo?.locationId || caseItem?.device?.locationId || '').trim()
    });
  }, [caseItem?.device, pendingDeviceInfo]);

  React.useEffect(() => {
    if (Array.isArray(devices) && devices.length > 0) {
      setVerificationInventory(devices);
    }
  }, [devices]);

  React.useEffect(() => {
    if (!deviceVerificationRequired || !canVerifySubmittedDevices) {
      setVerificationInventoryLoading(false);
      return;
    }

    let cancelled = false;
    setVerificationInventoryLoading(true);
    setVerificationInventoryError('');
    deviceApi
      .getAll({ includeInactive: true })
      .then((payload: any) => {
        if (cancelled) return;
        setVerificationInventory(Array.isArray(payload) ? payload : []);
      })
      .catch((error: any) => {
        if (cancelled) return;
        setVerificationInventoryError(error?.message || 'Could not load device inventory for verification.');
      })
      .finally(() => {
        if (!cancelled) setVerificationInventoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canVerifySubmittedDevices, caseItem?.id, deviceVerificationRequired]);

  React.useEffect(() => {
    let cancelled = false;
    const deviceId = String(caseItem?.device?.id || '').trim();
    const assignmentId = String(activeSlaAssignment?.id || '').trim();
    if (!slaAgreementViewerOpen || !deviceId || !assignmentId || !activeSlaHasSavedReport) {
      setDeviceSlaReport(null);
      setDeviceSlaReportLoading(false);
      setDeviceSlaReportError('');
      return () => {
        cancelled = true;
      };
    }

    setDeviceSlaReportLoading(true);
    setDeviceSlaReportError('');
    setDeviceSlaReport(null);

    deviceApi
      .getSlaReport(deviceId, { assignmentId })
      .then(payload => {
        if (cancelled) return;
        setDeviceSlaReport(normalizeCustomerFacingSlaReport(payload));
      })
      .catch(error => {
        if (cancelled) return;
        setDeviceSlaReportError(
          error instanceof Error ? error.message : 'Failed to load the saved SLA agreement.'
        );
      })
      .finally(() => {
        if (cancelled) return;
        setDeviceSlaReportLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeSlaAssignment?.id, activeSlaHasSavedReport, caseItem?.device?.id, slaAgreementViewerOpen]);

  const canEditWorkflow = canUpdateCaseStatus || canUpdateCasePriority || canAssignCases;
  const caseStatusKey = String(caseItem?.status || '').trim().toUpperCase();
  const caseIsClosed = caseStatusKey === 'CLOSED';
  const caseIsResolved = caseStatusKey === 'RESOLVED';
  const caseBlocksOperationalActions = caseIsClosed || caseIsResolved;
  const canShowFinalCloseAction = canCloseCases && !caseBlocksOperationalActions;
  const canShowFinalResolveAction = canCloseCases && caseIsClosed;
  const canShowFinalCaseAction = canShowFinalCloseAction || canShowFinalResolveAction;
  const finalActionBusy = Boolean(workflowClosing || workflowResolving || workflowSaving);
  const statusSelectDisabled = finalActionBusy || caseIsResolved;
  const workflowStatusOptions = caseIsClosed
    ? [
        { value: 'CLOSED', label: 'Closed' },
        { value: 'OPEN', label: 'Open' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'WAITING_CUSTOMER', label: 'Waiting Customer' },
        { value: 'WAITING_PARTS', label: 'Waiting Parts' },
        { value: 'ON_HOLD', label: 'On Hold' },
        { value: 'RESOLVED', label: 'Resolved' }
      ]
    : [
        { value: 'OPEN', label: 'Open' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'WAITING_CUSTOMER', label: 'Waiting Customer' },
        { value: 'WAITING_PARTS', label: 'Waiting Parts' },
        { value: 'ON_HOLD', label: 'On Hold' },
        { value: 'RESOLVED', label: 'Resolved' }
      ];
  const isAssigningCurrentCase = isAssigning && assigningCaseId === caseItem.id;
  const draftTaskGroup = String(workflowDraft?.taskGroup || '').trim();
  const hasTaskContext = Boolean(currentTask);
  const assigneeUnlocked = hasTaskContext || Boolean(draftTaskGroup);
  const taskAssigneeLabel = React.useMemo(() => {
    const directNames = Array.isArray(currentTask?.assignees)
      ? currentTask.assignees
          .map((entry: any) => String(entry?.fullName || entry?.username || '').trim())
          .filter(Boolean)
      : [];
    if (directNames.length > 0) {
      return Array.from(new Set(directNames)).join(', ');
    }

    const assignedIds = Array.isArray(currentTask?.assigneeUserIds)
      ? currentTask.assigneeUserIds.map((value: any) => String(value || '').trim()).filter(Boolean)
      : [];
    if (assignedIds.length > 0) {
      const resolved = assignedIds
        .map((id: string) => {
          const match = caseAssigneeOptions.find(
            (option: any) => option.id === id || option.username === id
          );
          return String(match?.fullName || match?.username || '').trim();
        })
        .filter(Boolean);
      if (resolved.length > 0) {
        return Array.from(new Set(resolved)).join(', ');
      }
      return assignedIds.length === 1 ? 'Assigned' : `Assigned (${assignedIds.length})`;
    }
    return 'Unassigned';
  }, [caseAssigneeOptions, currentTask?.assigneeUserIds, currentTask?.assignees]);
  const selectedAssigneeIds = React.useMemo(
    () =>
      Array.isArray(workflowDraft?.assigneeIds)
        ? workflowDraft.assigneeIds.map((value: any) => String(value || '').trim()).filter(Boolean)
        : [],
    [workflowDraft?.assigneeIds]
  );
  const selectedAssignees = React.useMemo(
    () =>
      selectedAssigneeIds.map((assigneeId: string) => {
        const match = caseAssigneeOptions.find(
          (option: any) => option.id === assigneeId || option.username === assigneeId
        );
        return {
          id: assigneeId,
          label: String(match?.fullName || match?.username || assigneeId).trim() || assigneeId
        };
      }),
    [caseAssigneeOptions, selectedAssigneeIds]
  );
  const workflowButtonLabel = workflowEditorOpen
    ? 'Close workflow editor'
    : hasTaskContext
    ? 'Edit workflow + assignments'
    : 'Assign task and user';
  const scheduledStartAt = React.useMemo(
    () => parseCaseScheduleDate(currentTask?.plannedStart),
    [currentTask?.plannedStart]
  );
  const scheduledEndAt = React.useMemo(
    () => parseCaseScheduleDate(currentTask?.plannedDue),
    [currentTask?.plannedDue]
  );
  const requestedStartAt = React.useMemo(
    () => parseCaseScheduleDate(currentTask?.scheduleRequestedStart),
    [currentTask?.scheduleRequestedStart]
  );
  const requestedEndAt = React.useMemo(
    () => parseCaseScheduleDate(currentTask?.scheduleRequestedDue),
    [currentTask?.scheduleRequestedDue]
  );
  const hasCommittedSchedule = Boolean(scheduledStartAt || scheduledEndAt);
  const hasSuggestedSchedule = Boolean(requestedStartAt || requestedEndAt);
  const hasScheduleRequest = hasSuggestedSchedule;
  const scheduleConfirmationStatus = String(currentTask?.scheduleConfirmationStatus || '').trim().toUpperCase();
  const isScheduleConfirmed =
    hasCommittedSchedule && !hasScheduleRequest && scheduleConfirmationStatus === 'CONFIRMED';
  const scheduleDisplayStartAt = hasScheduleRequest ? requestedStartAt : scheduledStartAt;
  const scheduleDisplayEndAt = hasScheduleRequest ? requestedEndAt : scheduledEndAt;
  const scheduleDisplayDuration = hasScheduleRequest
    ? formatCaseScheduleDuration(requestedStartAt, requestedEndAt)
    : formatCaseScheduleDuration(scheduledStartAt, scheduledEndAt);
  const scheduleUsesRequestedTone = hasScheduleRequest;
  const currentUserId = String((user as any)?.id || '').trim();
  const isPrivilegedScheduleRequester = ['ADMIN', 'SUPERUSER'].includes(viewerRoleKey);
  const isCurrentUserAssignedToTask = React.useMemo(() => {
    if (!currentTask || !currentUserId) return false;
    const assignedIds = new Set<string>();
    if (Array.isArray(currentTask.assigneeUserIds)) {
      currentTask.assigneeUserIds.forEach((value: any) => {
        const nextId = String(value || '').trim();
        if (nextId) assignedIds.add(nextId);
      });
    }
    if (Array.isArray(currentTask.assignees)) {
      currentTask.assignees.forEach((entry: any) => {
        const nextId = String(entry?.id || '').trim();
        if (nextId) assignedIds.add(nextId);
      });
    }
    const directAssignedUserId = String((currentTask as any)?.assignedUserId || '').trim();
    if (directAssignedUserId) assignedIds.add(directAssignedUserId);
    return assignedIds.has(currentUserId);
  }, [currentTask, currentUserId]);
  const canSubmitScheduleRequest =
    Boolean(currentTask?.id) &&
    !caseBlocksOperationalActions &&
    (isPrivilegedScheduleRequester || isCurrentUserAssignedToTask);
  const canConfirmSchedule =
    Boolean(currentTask?.id) &&
    hasCommittedSchedule &&
    !hasScheduleRequest &&
    !isScheduleConfirmed &&
    !caseBlocksOperationalActions &&
    !scheduleConfirmSaving &&
    (isPrivilegedScheduleRequester || isCurrentUserAssignedToTask);
  const canOpenScheduleRequest =
    canSubmitScheduleRequest &&
    !scheduleRequestSaving;
  const plannerStatusLabel =
    hasCommittedSchedule && hasScheduleRequest
      ? 'Change requested'
      : hasCommittedSchedule
      ? isScheduleConfirmed
        ? 'Confirmed'
        : 'Planned'
      : hasScheduleRequest
      ? 'Suggested schedule'
      : 'Not scheduled';
  const workflowSelectStyle = {
    ...styles.statusSelect,
    marginBottom: 0,
    borderRadius: '0.65rem',
    border: `1px solid ${CASE_BRAND_BORDER}`,
    padding: '0.58rem 0.68rem',
    backgroundColor: CASE_BRAND_CARD,
    color: CASE_BRAND_TEXT
  };
  const scheduleRequestStartPreview = React.useMemo(
    () => combineCaseDateTimeInput(scheduleRequestDraft?.startDate || '', scheduleRequestDraft?.startTime || ''),
    [scheduleRequestDraft?.startDate, scheduleRequestDraft?.startTime]
  );
  const scheduleRequestEndPreview = React.useMemo(
    () => combineCaseDateTimeInput(scheduleRequestDraft?.endDate || '', scheduleRequestDraft?.endTime || ''),
    [scheduleRequestDraft?.endDate, scheduleRequestDraft?.endTime]
  );
  const scheduleRequestInputStyle = {
    ...workflowSelectStyle,
    minHeight: '3.2rem',
    padding: '0.82rem 0.95rem',
    borderRadius: '0.72rem',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.2,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.01em'
  };
  const scheduleRequestActionButtonStyle = {
    ...styles.assignmentButton,
    borderRadius: '0.55rem',
    padding: '0.72rem 1.05rem',
    minHeight: '2.95rem',
    fontSize: '0.95rem',
    fontWeight: 700
  };
  const requestedScheduleTone = {
    border: 'rgba(166, 59, 50, 0.22)',
    background: '#FFF7F5',
    text: '#A63B32'
  };
  const quickViewDeviceModelLabel =
    String(caseItem?.device?.modelNumber || pendingDeviceInfo?.modelNumber || '').trim() ||
    'Pending device';
  const quickViewDeviceSerialLabel =
    String(caseItem?.device?.serialNumber || pendingDeviceInfo?.serialNumber || '').trim();
  const quickViewDeviceAssetLabel =
    String((caseItem?.device as any)?.assetNumber || pendingDeviceInfo?.assetNumber || '').trim();
  const quickViewDeviceIdentifierLabel = quickViewDeviceAssetLabel
    ? `Asset ${quickViewDeviceAssetLabel}${quickViewDeviceSerialLabel ? ` | Serial ${quickViewDeviceSerialLabel}` : ''}`
    : quickViewDeviceSerialLabel || 'Submitted identifier pending verification';

  const openScheduleRequestModal = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (!currentTask?.id || !canOpenScheduleRequest) return;
    setScheduleRequestError('');
    setScheduleRequestDraft(
      buildScheduleRequestDraft(
        requestedStartAt || scheduledStartAt,
        requestedEndAt || scheduledEndAt
      )
    );
    setScheduleRequestModalOpen(true);
  };

  const updateScheduleRequestStartField = (
    field: 'startDate' | 'startTime',
    value: string
  ) => {
    setScheduleRequestDraft(current => {
      if (!current) return current;
      const currentDurationMinutes = getScheduleRequestDurationMinutes(current);
      const nextDraft = alignScheduleRequestEndToStart({
        ...current,
        [field]: value,
        validationMessage: null
      }, currentDurationMinutes);
      return nextDraft;
    });
  };

  const updateScheduleRequestEndField = (
    field: 'endDate' | 'endTime',
    value: string
  ) => {
    setScheduleRequestDraft(current =>
      current
        ? {
            ...current,
            [field]: value,
            validationMessage: null
          }
        : current
    );
  };

  const closeScheduleRequestModal = () => {
    if (scheduleRequestSaving) return;
    setScheduleRequestModalOpen(false);
    setScheduleRequestDraft(null);
  };

  const saveScheduleRequest = async () => {
    if (!currentTask?.id || !scheduleRequestDraft) return;
    const requestedStart = combineCaseDateTimeInput(
      scheduleRequestDraft.startDate,
      scheduleRequestDraft.startTime
    );
    const requestedDue = combineCaseDateTimeInput(
      scheduleRequestDraft.endDate,
      scheduleRequestDraft.endTime
    );
    if (!requestedStart) {
      setScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: 'Requested start date and time are required.' } : current
      );
      return;
    }
    if (!requestedDue) {
      setScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: 'Requested finish date and time are required.' } : current
      );
      return;
    }
    if (requestedDue.getTime() <= requestedStart.getTime()) {
      setScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: 'Requested finish must be after requested start.' } : current
      );
      return;
    }

    setScheduleRequestSaving(true);
    setScheduleRequestError('');
    try {
      const payload = await caseApi.requestTaskSchedule(currentTask.id, {
        requestedStart: requestedStart.toISOString(),
        requestedDue: requestedDue.toISOString()
      });
      const refreshedCase =
        payload?.case || mergeCaseTaskUpdate(caseItem, payload?.task || null) || (await caseApi.getById(caseItem.id));
      setScheduleRequestModalOpen(false);
      setScheduleRequestDraft(null);
      applyCaseQuickViewRefresh(refreshedCase || caseItem);
    } catch (error: any) {
      const message = error?.message || 'Failed to request scheduling for this task.';
      setScheduleRequestError(message);
      setScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: message } : current
      );
    } finally {
      setScheduleRequestSaving(false);
    }
  };

  const confirmTaskSchedule = async () => {
    if (!currentTask?.id || !canConfirmSchedule) return;
    setScheduleConfirmSaving(true);
    setScheduleRequestError('');
    try {
      const payload = await caseApi.confirmTaskSchedule(currentTask.id);
      const refreshedCase =
        payload?.case || mergeCaseTaskUpdate(caseItem, payload?.task || null) || (await caseApi.getById(caseItem.id));
      applyCaseQuickViewRefresh(refreshedCase || caseItem);
    } catch (error: any) {
      setScheduleRequestError(error?.message || 'Failed to confirm this scheduled visit.');
    } finally {
      setScheduleConfirmSaving(false);
    }
  };

  const closeSurveyResponseDetail = () => {
    setSurveyResponseDetail(null);
    setSurveyResponseError('');
  };

  const openSurveyResponseDetail = async (event: React.MouseEvent<HTMLButtonElement>, survey: any) => {
    event.stopPropagation();
    const surveyId = String(survey?.id || '').trim();
    const surveyStatus = String(survey?.status || '').trim().toUpperCase();
    if (!surveyId || surveyStatus !== 'COMPLETED') return;
    if (!canViewSurveyResponseDetails) {
      setSurveyResponseError('Only admins and superusers can view detailed survey responses.');
      return;
    }

    setSurveyResponseLoadingId(surveyId);
    setSurveyResponseError('');
    try {
      const detail = (await caseApi.getSurveyResponseDetail(surveyId)) as CaseSurveyResponseDetail;
      setSurveyResponseDetail(detail);
    } catch (error: any) {
      setSurveyResponseError(error?.message || 'Failed to load survey responses.');
    } finally {
      setSurveyResponseLoadingId(null);
    }
  };

  const normalizeVerificationIdentifier = (value: any) => String(value || '').trim().toLowerCase();
  const formatVerificationCustomerName = (customer: any) =>
    String(customer?.companyName || customer?.company || customer?.email || 'Customer').trim() || 'Customer';
  const caseCustomerId = String(caseItem?.customerId || caseItem?.customer?.id || '').trim();
  const caseCustomerName = formatVerificationCustomerName(caseItem?.customer);
  const submittedContact = React.useMemo(() => {
    const contacts = Array.isArray(caseItem?.customer?.contacts) ? caseItem.customer.contacts : [];
    const primaryContact = contacts.find((entry: any) => entry?.isPrimary) || contacts[0] || null;
    return {
      name:
        String(caseItem?.createdByName || primaryContact?.fullName || '').trim() ||
        'Customer contact',
      email:
        String(caseItem?.contactEmail || primaryContact?.email || caseItem?.customer?.email || '').trim(),
      phone:
        String(caseItem?.contactPhone || primaryContact?.phone || caseItem?.customer?.phone || '').trim()
    };
  }, [caseItem]);
  const customerLocations = React.useMemo(() => {
    const locations = Array.isArray(caseItem?.customer?.locations) ? caseItem.customer.locations : [];
    return locations
      .filter((location: any) => location?.id && location?.isActive !== false)
      .slice()
      .sort((a: any, b: any) => {
        if (Boolean(a?.isPrimary) !== Boolean(b?.isPrimary)) {
          return a?.isPrimary ? -1 : 1;
        }
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      });
  }, [caseItem?.customer?.locations]);
  const getVerificationDeviceCustomerId = (device: any) =>
    String(device?.customerId || device?.customer?.id || '').trim();
  const getVerificationDeviceLocationLabel = (device: any) =>
    String(device?.location?.name || device?.locationName || '').trim();
  const getVerificationDeviceModelLabel = (device: any) =>
    String(
      device?.modelNumber ||
        device?.model?.modelNumber ||
        device?.modelName ||
        device?.model?.modelName ||
        'Device'
    ).trim() || 'Device';
  const buildVerificationDeviceIdentity = (device: any) => {
    const serial = String(device?.serialNumber || '').trim();
    const asset = String(device?.assetNumber || '').trim();
    if (asset && serial) return `Asset ${asset} | Serial ${serial}`;
    if (asset) return `Asset ${asset}`;
    if (serial) return `Serial ${serial}`;
    return 'No serial or asset recorded';
  };
  const allVerificationDevices = React.useMemo(() => {
    const byId = new Map<string, any>();
    [...(Array.isArray(devices) ? devices : []), ...verificationInventory].forEach((device: any) => {
      const id = String(device?.id || '').trim();
      if (id) byId.set(id, device);
    });
    return Array.from(byId.values());
  }, [devices, verificationInventory]);
  const submittedSerialForCheck = normalizeVerificationIdentifier(
    pendingDeviceInfo?.serialNumber || verificationDraft.serialNumber
  );
  const submittedAssetForCheck = normalizeVerificationIdentifier(
    pendingDeviceInfo?.assetNumber || verificationDraft.assetNumber
  );
  const identifierMatches = React.useMemo(() => {
    const matches: Array<{ key: string; device: any; reason: string; severity: 'warning' | 'info' }> = [];
    const seen = new Set<string>();
    const pushMatch = (device: any, reason: string, severity: 'warning' | 'info' = 'warning') => {
      const id = String(device?.id || '').trim();
      const key = `${id || Math.random().toString(36)}:${reason}`;
      if (!id || seen.has(key) || id === String(caseItem?.device?.id || '').trim()) return;
      seen.add(key);
      matches.push({ key, device, reason, severity });
    };
    allVerificationDevices.forEach((device: any) => {
      const deviceSerial = normalizeVerificationIdentifier(device?.serialNumber);
      const deviceAsset = normalizeVerificationIdentifier(device?.assetNumber);
      if (submittedSerialForCheck && deviceSerial && submittedSerialForCheck === deviceSerial) {
        pushMatch(device, 'Submitted serial matches this device serial');
      }
      if (submittedSerialForCheck && deviceAsset && submittedSerialForCheck === deviceAsset) {
        pushMatch(device, 'Submitted serial matches this device asset number');
      }
      if (submittedAssetForCheck && deviceAsset && submittedAssetForCheck === deviceAsset) {
        pushMatch(device, 'Submitted asset matches this device asset number');
      }
      if (submittedAssetForCheck && deviceSerial && submittedAssetForCheck === deviceSerial) {
        pushMatch(device, 'Submitted asset matches this device serial');
      }
    });
    return matches;
  }, [allVerificationDevices, caseItem?.device?.id, submittedAssetForCheck, submittedSerialForCheck]);
  const customerVerificationDevices = React.useMemo(() => {
    const currentDeviceId = String(caseItem?.device?.id || '').trim();
    return allVerificationDevices
      .filter((device: any) => {
        if (!caseCustomerId) return false;
        const id = String(device?.id || '').trim();
        if (!id || id === currentDeviceId) return false;
        return getVerificationDeviceCustomerId(device) === caseCustomerId;
      })
      .slice()
      .sort((a: any, b: any) => {
        const modelCompare = getVerificationDeviceModelLabel(a).localeCompare(getVerificationDeviceModelLabel(b));
        if (modelCompare !== 0) return modelCompare;
        return buildVerificationDeviceIdentity(a).localeCompare(buildVerificationDeviceIdentity(b));
      });
  }, [allVerificationDevices, caseCustomerId, caseItem?.device?.id]);
  const verificationDeviceOptions = React.useMemo(() => {
    const currentDeviceId = String(caseItem?.device?.id || '').trim();
    const matchDeviceIds = new Set(identifierMatches.map(match => String(match.device?.id || '').trim()).filter(Boolean));
    return allVerificationDevices
      .filter((device: any) => {
        const id = String(device?.id || '').trim();
        if (!id || id === currentDeviceId) return false;
        const deviceCustomerId = getVerificationDeviceCustomerId(device);
        return !deviceCustomerId || deviceCustomerId === caseCustomerId || matchDeviceIds.has(id);
      })
      .slice()
      .sort((a: any, b: any) => {
        const aLabel = getVerificationDeviceModelLabel(a).localeCompare(getVerificationDeviceModelLabel(b));
        if (aLabel !== 0) return aLabel;
        return String(a?.serialNumber || '').localeCompare(String(b?.serialNumber || ''));
      });
  }, [allVerificationDevices, caseCustomerId, caseItem?.device?.id, identifierMatches]);

  const formatVerificationDeviceOption = (device: any) => {
    const model = getVerificationDeviceModelLabel(device);
    const identity = buildVerificationDeviceIdentity(device);
    const customer = formatVerificationCustomerName(device?.customer);
    return [model, identity, customer].filter(Boolean).join(' - ');
  };
  const verificationChecklistComplete =
    verificationChecks.identifiers && verificationChecks.customer && verificationChecks.model;

  const submitDeviceVerification = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!deviceVerificationRequired || !canVerifySubmittedDevices || typeof verifySubmittedDevice !== 'function') {
      return;
    }
    if (verificationMode === 'link' && !verificationDraft.deviceId) {
      setVerificationError('Select an existing registered device before confirming.');
      return;
    }
    if (verificationMode === 'create' && !verificationDraft.serialNumber.trim()) {
      setVerificationError('Serial number is required to create the verified device.');
      return;
    }
    if (!verificationChecklistComplete) {
      setVerificationError('Complete the verification checklist before unlocking this case.');
      return;
    }
    setVerificationSaving(true);
    setVerificationError('');
    try {
      if (verificationMode === 'link') {
        await verifySubmittedDevice(caseItem, { deviceId: verificationDraft.deviceId });
      } else {
        await verifySubmittedDevice(caseItem, {
          modelNumber: verificationDraft.modelNumber.trim() || 'Other',
          manufacturer: verificationDraft.manufacturer.trim() || null,
          serialNumber: verificationDraft.serialNumber.trim(),
          assetNumber: verificationDraft.assetNumber.trim() || null,
          locationId: verificationDraft.locationId.trim() || null
        });
      }
    } catch (error: any) {
      setVerificationError(error?.message || 'Failed to verify this device.');
    } finally {
      setVerificationSaving(false);
    }
  };

  const renderDeviceVerificationGate = () => {
    const submittedSerial = String(pendingDeviceInfo?.serialNumber || verificationDraft.serialNumber || '').trim();
    const submittedAsset = String(pendingDeviceInfo?.assetNumber || verificationDraft.assetNumber || '').trim();
    const submittedModel = String(pendingDeviceInfo?.modelNumber || verificationDraft.modelNumber || 'Other').trim();
    const resolutionNote = String(pendingDeviceInfo?.resolutionNote || '').trim();
    const inputStyle = {
      ...workflowSelectStyle,
      width: '100%',
      minHeight: '2.7rem'
    };
    const modeButtonStyle = (active: boolean) => ({
      ...styles.assignmentButton,
      ...(active ? styles.assignmentButtonPrimary : styles.assignmentButtonGhost),
      borderRadius: '0.65rem',
      minHeight: '2.45rem'
    });
    const evidenceCardStyle = {
      border: `1px solid ${CASE_BRAND_BORDER}`,
      borderRadius: '0.7rem',
      backgroundColor: CASE_BRAND_CARD,
      padding: '0.7rem',
      display: 'grid',
      gap: '0.25rem'
    };
    const evidenceLabelStyle = {
      color: CASE_BRAND_MUTED,
      fontSize: '0.72rem',
      fontWeight: 800,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.08em'
    };
    const evidenceValueStyle = {
      color: CASE_BRAND_TEXT,
      fontWeight: 700,
      lineHeight: 1.3
    };
    const setLinkDevice = (deviceId: string) => {
      setVerificationMode('link');
      setVerificationDraft(current => ({ ...current, deviceId }));
      setVerificationChecks(current => ({ ...current, identifiers: true }));
      setVerificationError('');
    };

    return (
      <div
        style={{
          border: '1px solid rgba(217, 154, 51, 0.38)',
          borderRadius: '0.85rem',
          backgroundColor: '#FFF7E7',
          padding: '1rem',
          display: 'grid',
          gap: '0.9rem'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: '0.25rem' }}>
            <div style={{ color: CASE_BRAND_TEXT, fontSize: '1.08rem', fontWeight: 800 }}>
              Device Verification Required
            </div>
            <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.9rem', lineHeight: 1.45 }}>
              Confirm this submitted device before scheduling, assigning, or processing the case.
            </div>
          </div>
          <span
            style={{
              ...styles.assignmentChip,
              backgroundColor: CASE_BRAND_ACCENT_BG,
              border: '1px solid rgba(138, 97, 30, 0.22)',
              color: CASE_BRAND_ACCENT_TEXT
            }}
          >
            Verification hold
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.65rem'
          }}
        >
          <div style={evidenceCardStyle}>
            <div style={evidenceLabelStyle}>Submitted model</div>
            <div style={evidenceValueStyle}>{submittedModel || 'Not provided'}</div>
          </div>
          <div style={evidenceCardStyle}>
            <div style={evidenceLabelStyle}>Submitted serial</div>
            <div style={evidenceValueStyle}>{submittedSerial || 'Not provided'}</div>
          </div>
          <div style={evidenceCardStyle}>
            <div style={evidenceLabelStyle}>Submitted asset</div>
            <div style={evidenceValueStyle}>{submittedAsset || 'Not provided'}</div>
          </div>
        </div>

        {resolutionNote ? (
          <div
            style={{
              padding: '0.75rem 0.85rem',
              borderRadius: '0.7rem',
              border: '1px solid rgba(166, 59, 50, 0.18)',
              backgroundColor: '#FFF1ED',
              color: '#A63B32',
              fontSize: '0.88rem',
              lineHeight: 1.45
            }}
          >
            {resolutionNote}
          </div>
        ) : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '0.75rem'
          }}
        >
          <div style={evidenceCardStyle}>
            <div style={evidenceLabelStyle}>Customer contact</div>
            <div style={evidenceValueStyle}>{submittedContact.name}</div>
            <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.86rem', display: 'grid', gap: '0.2rem' }}>
              {submittedContact.email ? (
                <a href={`mailto:${submittedContact.email}`} style={{ color: CASE_BRAND_TEXT }}>
                  {submittedContact.email}
                </a>
              ) : (
                <span>No email recorded on this case</span>
              )}
              {submittedContact.phone ? (
                <a href={`tel:${submittedContact.phone}`} style={{ color: CASE_BRAND_TEXT }}>
                  {submittedContact.phone}
                </a>
              ) : (
                <span>No phone recorded on this case</span>
              )}
            </div>
          </div>
          <div style={evidenceCardStyle}>
            <div style={evidenceLabelStyle}>Customer and location</div>
            <div style={evidenceValueStyle}>{caseCustomerName}</div>
            <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.86rem' }}>
              {customerLocations.length > 0
                ? `${customerLocations.length} customer location${customerLocations.length === 1 ? '' : 's'} available`
                : 'No customer locations listed; device will be verified at customer level.'}
            </div>
          </div>
        </div>

        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.75rem',
            backgroundColor: CASE_BRAND_CARD,
            padding: '0.85rem',
            display: 'grid',
            gap: '0.7rem'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'grid', gap: '0.2rem' }}>
              <div style={{ color: CASE_BRAND_TEXT, fontWeight: 800 }}>Identifier check</div>
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.86rem' }}>
                Checks submitted serial and asset against existing serials and assets, including swapped entries.
              </div>
            </div>
            <button
              type="button"
              style={{ ...styles.assignmentButton, ...styles.assignmentButtonGhost, minHeight: '2.4rem' }}
              onClick={() => setCustomerDeviceReviewOpen(true)}
              disabled={verificationInventoryLoading}
            >
              Review other customer devices ({customerVerificationDevices.length})
            </button>
          </div>

          {verificationInventoryLoading ? (
            <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.86rem' }}>Checking device inventory...</div>
          ) : verificationInventoryError ? (
            <div style={{ color: '#b91c1c', fontSize: '0.86rem', fontWeight: 700 }}>
              {verificationInventoryError}
            </div>
          ) : identifierMatches.length > 0 ? (
            <div style={{ display: 'grid', gap: '0.55rem' }}>
              {identifierMatches.slice(0, 6).map(match => {
                const matchCustomerId = getVerificationDeviceCustomerId(match.device);
                const sameCustomer = Boolean(matchCustomerId && matchCustomerId === caseCustomerId);
                const unassigned = !matchCustomerId;
                const canLinkMatch = sameCustomer || unassigned;
                return (
                  <div
                    key={match.key}
                    style={{
                      border: '1px solid rgba(217, 154, 51, 0.45)',
                      borderRadius: '0.7rem',
                      backgroundColor: '#FFF8E8',
                      padding: '0.7rem',
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: '0.65rem',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'grid', gap: '0.2rem' }}>
                      <div style={{ color: CASE_BRAND_ACCENT_TEXT, fontSize: '0.78rem', fontWeight: 800 }}>
                        {match.reason}
                      </div>
                      <div style={{ color: CASE_BRAND_TEXT, fontWeight: 800 }}>
                        {getVerificationDeviceModelLabel(match.device)}
                      </div>
                      <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.86rem' }}>
                        {buildVerificationDeviceIdentity(match.device)}
                        {' | '}
                        {unassigned ? 'Unassigned device' : `Customer: ${formatVerificationCustomerName(match.device?.customer)}`}
                        {getVerificationDeviceLocationLabel(match.device)
                          ? ` | Location: ${getVerificationDeviceLocationLabel(match.device)}`
                          : ''}
                      </div>
                      {!canLinkMatch ? (
                        <div style={{ color: '#A63B32', fontSize: '0.82rem', fontWeight: 700 }}>
                          This match is assigned to another customer. Confirm whether the submission or customer is wrong before creating a new device.
                        </div>
                      ) : null}
                    </div>
                    {canLinkMatch ? (
                      <button
                        type="button"
                        style={{ ...styles.assignmentButton, ...styles.assignmentButtonPrimary }}
                        onClick={() => setLinkDevice(String(match.device.id || ''))}
                        disabled={verificationSaving}
                      >
                        Link this device
                      </button>
                    ) : null}
                  </div>
                );
              })}
              {identifierMatches.length > 6 ? (
                <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.84rem' }}>
                  {identifierMatches.length - 6} more matches are available in device inventory.
                </div>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                border: `1px dashed ${CASE_BRAND_BORDER}`,
                borderRadius: '0.7rem',
                padding: '0.7rem',
                color: CASE_BRAND_MUTED,
                fontSize: '0.86rem',
                backgroundColor: '#FFFFFF'
              }}
            >
              No exact serial or asset matches found in the loaded device inventory.
            </div>
          )}
        </div>

        {!canVerifySubmittedDevices ? (
          <div
            style={{
              padding: '0.8rem 0.9rem',
              borderRadius: '0.75rem',
              border: `1px dashed ${CASE_BRAND_BORDER}`,
              backgroundColor: CASE_BRAND_CARD,
              color: CASE_BRAND_MUTED,
              fontWeight: 700
            }}
          >
            Admin or superuser verification is required before this case can proceed.
          </div>
        ) : (
          <form onSubmit={submitDeviceVerification} style={{ display: 'grid', gap: '0.85rem' }}>
            <div
              style={{
                border: `1px solid ${CASE_BRAND_BORDER}`,
                borderRadius: '0.75rem',
                backgroundColor: CASE_BRAND_CARD,
                padding: '0.85rem',
                display: 'grid',
                gap: '0.55rem'
              }}
            >
              <div style={{ color: CASE_BRAND_TEXT, fontWeight: 800 }}>Verification checklist</div>
              {[
                ['identifiers', 'Serial/asset checked against existing devices'],
                ['customer', 'Customer contact and ownership confirmed'],
                ['model', 'Device model, serial, asset, and location confirmed']
              ].map(([key, label]) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.55rem',
                    color: CASE_BRAND_TEXT,
                    fontSize: '0.9rem',
                    fontWeight: 700
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean((verificationChecks as any)[key])}
                    onChange={event =>
                      setVerificationChecks(current => ({
                        ...current,
                        [key]: event.target.checked
                      }))
                    }
                    disabled={verificationSaving}
                  />
                  {label}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.55rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                style={modeButtonStyle(verificationMode === 'create')}
                onClick={() => {
                  setVerificationMode('create');
                  setVerificationError('');
                }}
                disabled={verificationSaving}
              >
                Create verified device
              </button>
              <button
                type="button"
                style={modeButtonStyle(verificationMode === 'link')}
                onClick={() => {
                  setVerificationMode('link');
                  setVerificationError('');
                }}
                disabled={verificationSaving}
              >
                Link existing device
              </button>
            </div>

            {verificationMode === 'create' ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '0.7rem'
                }}
              >
                <label style={{ display: 'grid', gap: '0.3rem', color: CASE_BRAND_TEXT, fontWeight: 700, fontSize: '0.82rem' }}>
                  Verified device model
                  <input
                    type="text"
                    style={inputStyle}
                    value={verificationDraft.modelNumber}
                    onChange={event => setVerificationDraft(current => ({ ...current, modelNumber: event.target.value }))}
                    disabled={verificationSaving}
                  />
                </label>
                <label style={{ display: 'grid', gap: '0.3rem', color: CASE_BRAND_TEXT, fontWeight: 700, fontSize: '0.82rem' }}>
                  Verified manufacturer
                  <input
                    type="text"
                    style={inputStyle}
                    value={verificationDraft.manufacturer}
                    onChange={event => setVerificationDraft(current => ({ ...current, manufacturer: event.target.value }))}
                    disabled={verificationSaving}
                  />
                </label>
                <label style={{ display: 'grid', gap: '0.3rem', color: CASE_BRAND_TEXT, fontWeight: 700, fontSize: '0.82rem' }}>
                  Verified serial number
                  <input
                    type="text"
                    style={inputStyle}
                    value={verificationDraft.serialNumber}
                    onChange={event => setVerificationDraft(current => ({ ...current, serialNumber: event.target.value }))}
                    disabled={verificationSaving}
                    required
                  />
                </label>
                <label style={{ display: 'grid', gap: '0.3rem', color: CASE_BRAND_TEXT, fontWeight: 700, fontSize: '0.82rem' }}>
                  Verified asset number
                  <input
                    type="text"
                    style={inputStyle}
                    value={verificationDraft.assetNumber}
                    onChange={event => setVerificationDraft(current => ({ ...current, assetNumber: event.target.value }))}
                    disabled={verificationSaving}
                  />
                </label>
                <label style={{ display: 'grid', gap: '0.3rem', color: CASE_BRAND_TEXT, fontWeight: 700, fontSize: '0.82rem' }}>
                  Verified location
                  {customerLocations.length > 0 ? (
                    <select
                      style={inputStyle}
                      value={verificationDraft.locationId}
                      onChange={event => setVerificationDraft(current => ({ ...current, locationId: event.target.value }))}
                      disabled={verificationSaving}
                    >
                      <option value="">Customer level</option>
                      {customerLocations.map((location: any) => (
                        <option key={location.id} value={location.id}>
                          {location.name || 'Customer location'}
                          {location.isPrimary ? ' (primary)' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div
                      style={{
                        ...inputStyle,
                        display: 'flex',
                        alignItems: 'center',
                        color: CASE_BRAND_MUTED,
                        backgroundColor: '#F8FAF9'
                      }}
                    >
                      Customer level
                    </div>
                  )}
                </label>
              </div>
            ) : (
              <label style={{ display: 'grid', gap: '0.3rem', color: CASE_BRAND_TEXT, fontWeight: 700, fontSize: '0.82rem' }}>
                Existing registered device
                <select
                  style={inputStyle}
                  value={verificationDraft.deviceId}
                  onChange={event => setVerificationDraft(current => ({ ...current, deviceId: event.target.value }))}
                  disabled={verificationSaving}
                >
                  <option value="">Select a device...</option>
                  {verificationDeviceOptions.map((device: any) => (
                    <option key={device.id} value={device.id}>
                      {formatVerificationDeviceOption(device)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {verificationError ? (
              <div style={{ color: '#b91c1c', fontSize: '0.86rem', fontWeight: 700 }}>
                {verificationError}
              </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.65rem', flexWrap: 'wrap' }}>
              <button
                type="submit"
                style={{
                  ...styles.assignmentButton,
                  ...styles.assignmentButtonPrimary,
                  minHeight: '2.7rem',
                  ...(!verificationChecklistComplete ? { opacity: 0.62, cursor: 'not-allowed' } : {})
                }}
                disabled={verificationSaving || !verificationChecklistComplete}
              >
                {verificationSaving ? 'Verifying...' : 'Verify device and unlock case'}
              </button>
            </div>
          </form>
        )}

        {customerDeviceReviewOpen ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(17, 24, 39, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              zIndex: 2400
            }}
            onClick={event => event.stopPropagation()}
          >
            <div
              style={{
                width: 'min(920px, 100%)',
                maxHeight: '82vh',
                overflowY: 'auto',
                borderRadius: '0.95rem',
                backgroundColor: CASE_BRAND_CARD,
                border: `1px solid ${CASE_BRAND_BORDER}`,
                boxShadow: '0 24px 50px rgba(15, 23, 42, 0.24)',
                padding: '1rem',
                display: 'grid',
                gap: '0.85rem'
              }}
              onClick={event => event.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ display: 'grid', gap: '0.2rem' }}>
                  <div style={{ color: CASE_BRAND_TEXT, fontSize: '1.25rem', fontWeight: 800 }}>
                    Other registered customer devices
                  </div>
                  <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.9rem' }}>
                    {caseCustomerName}
                  </div>
                </div>
                <button
                  type="button"
                  style={styles.caseQuickViewCloseButton}
                  onClick={() => setCustomerDeviceReviewOpen(false)}
                >
                  Close
                </button>
              </div>
              {customerVerificationDevices.length === 0 ? (
                <div
                  style={{
                    border: `1px dashed ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    padding: '0.9rem',
                    color: CASE_BRAND_MUTED
                  }}
                >
                  No other registered devices are listed for this customer in the loaded inventory.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '0.55rem' }}>
                  {customerVerificationDevices.map((device: any) => (
                    <div
                      key={device.id}
                      style={{
                        border: `1px solid ${CASE_BRAND_BORDER}`,
                        borderRadius: '0.75rem',
                        backgroundColor: CASE_BRAND_PANEL,
                        padding: '0.75rem',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '0.75rem',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'grid', gap: '0.2rem' }}>
                        <div style={{ color: CASE_BRAND_TEXT, fontWeight: 800 }}>
                          {getVerificationDeviceModelLabel(device)}
                        </div>
                        <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.88rem' }}>
                          {buildVerificationDeviceIdentity(device)}
                          {getVerificationDeviceLocationLabel(device)
                            ? ` | ${getVerificationDeviceLocationLabel(device)}`
                            : ''}
                          {device.lifecycleStatus ? ` | ${device.lifecycleStatus}` : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        style={{ ...styles.assignmentButton, ...styles.assignmentButtonPrimary }}
                        onClick={() => {
                          setLinkDevice(String(device.id || ''));
                          setCustomerDeviceReviewOpen(false);
                        }}
                        disabled={verificationSaving}
                      >
                        Link this device
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderSafetyUpgradeWorkspace = () => {
    return (
      <div style={styles.repairWorkflowPanel}>
        <div style={styles.repairWorkflowHeader}>
          <span style={styles.repairWorkflowHint}>Safety / upgrade workflow</span>
          <span style={styles.repairWorkflowNote}>
            {caseMedia.length > 0 ? `${caseMedia.length} notice document${caseMedia.length === 1 ? '' : 's'} attached` : 'No notice documents attached'}
          </span>
        </div>
        <div style={styles.repairWorkflowActions}>
          <button
            type="button"
            style={{ ...styles.button, ...styles.caseActionButtonPrimary }}
            onClick={async event => {
              event.stopPropagation();
              const allowed = await guardTaskWorkspaceOpen(currentTask);
              if (!allowed) return;
              navigate(`/safety-upgrade/${caseItem.id}`);
            }}
            disabled={disableCaseNavigation}
          >
            Workflow Preparation
          </button>
          <button
            type="button"
            style={{ ...styles.button, ...styles.caseActionButtonSecondary }}
            onClick={async event => {
              event.stopPropagation();
              const allowed = await guardTaskWorkspaceOpen(currentTask);
              if (!allowed) return;
              navigate(`/safety-upgrade/${caseItem.id}/implementation`);
            }}
            disabled={disableCaseNavigation}
          >
            Implementation
          </button>
        </div>
      </div>
    );
  };

  const renderTaskWorkspaceActions = () => {
    if (!hasTaskContext) {
      if (canAssignCases) {
        return (
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            <div style={{ fontSize: '0.92rem', color: CASE_BRAND_TEXT, fontWeight: 700 }}>
              No task is currently assigned
            </div>
            <div style={{ fontSize: '0.84rem', color: CASE_BRAND_MUTED }}>
              Open the workflow editor to choose task group, status, priority, and assignees.
            </div>
          </div>
        );
      }
      return (
        <div style={styles.assignmentPlaceholder}>
          No active task is assigned yet. Use the workflow editor to choose a task group and assignees.
        </div>
      );
    }

    if (activeGroupCode === 'INSTALL') {
      return (
        <div style={styles.installPhasePanel}>
          <div style={styles.installPhaseHeader}>
            <span style={styles.installPhaseHint}>Installation phases</span>
            <span style={styles.installPhaseNote}>Open a workspace to continue progress</span>
          </div>
          <div style={styles.installPhaseButtons}>
            {INSTALL_PHASES.map((phase: any) => (
              <button
                key={phase.id}
                type="button"
                style={{
                  ...styles.installPhaseButton,
                  borderColor: phase.color,
                  color: phase.color,
                  cursor: phase.actionable ? 'pointer' : 'not-allowed',
                  opacity: phase.actionable ? 1 : 0.6
                }}
                disabled={!phase.actionable}
                onClick={async event => {
                  event.stopPropagation();
                  if (!phase.actionable) return;
                  handleOpenInstallPhase(caseItem.id, phase.id);
                }}
                title={phase.actionable ? 'Open phase workspace' : 'Coming soon'}
              >
                {phase.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (activeGroupCode === 'MAINTENANCE' && MAINTENANCE_VARIANTS.length > 0) {
      return (
        <div style={styles.maintenanceVariantPanel}>
          <div style={styles.maintenanceVariantHeader}>
            <span style={styles.maintenanceVariantHint}>Maintenance modes</span>
            <span style={styles.maintenanceVariantNote}>Select the right maintenance visit type</span>
          </div>
          <div style={styles.assignmentVariantRow}>
            {MAINTENANCE_VARIANTS.map((variant: any) => {
              const isVariantActive = taskTypeCodeRaw === variant.code;
              const variantRouteBuilder = MAINTENANCE_VARIANT_ROUTES[variant.code];
              return (
                <button
                  key={variant.code}
                  type="button"
                  style={{
                    ...styles.assignmentVariantButton,
                    borderColor: MAINTENANCE_COLOR,
                    color: MAINTENANCE_COLOR,
                    ...(isVariantActive
                      ? {
                          backgroundColor: MAINTENANCE_COLOR,
                          color: '#ffffff'
                        }
                      : {}),
                    ...(isAssigning ? styles.assignmentButtonDisabled : {})
                  }}
                  onClick={async event => {
                    event.stopPropagation();
                    if (!caseItem.id) return;
                    if (variantRouteBuilder) {
                      const allowed = await guardTaskWorkspaceOpen(currentTask);
                      if (!allowed) return;
                      navigate(variantRouteBuilder(caseItem.id));
                      return;
                    }
                    if (!canAssignCases) return;
                    handleAssignTaskGroup(caseItem.id, variant.code);
                  }}
                  disabled={(isAssigning && !isVariantActive) || (!variantRouteBuilder && !canAssignCases)}
                >
                  {variant.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (activeGroupCode === 'REPAIR') {
      return (
        <div style={styles.repairWorkflowPanel}>
          <div style={styles.repairWorkflowHeader}>
            <span style={styles.repairWorkflowHint}>Repair workflow</span>
            <span style={styles.repairWorkflowNote}>
              {analysisMeta.variant === 'accepted'
                ? 'TSFAD accepted'
                : analysisMeta.variant === 'pending'
                ? 'TSFAD ready for review'
                : 'TSFAD guidance coming soon'}
            </span>
          </div>
          <div style={styles.repairWorkflowActions}>
            <button
              type="button"
              style={{ ...styles.button, ...styles.caseActionButtonPrimary }}
              onClick={async event => {
                event.stopPropagation();
                if (!casePath) return;
                const allowed = await guardTaskWorkspaceOpen(currentTask);
                if (!allowed) return;
                navigate(`/tsfad/${casePath}`);
              }}
              disabled={disableCaseNavigation}
            >
              {reviewLabel}
            </button>
            <button
              type="button"
              style={{ ...styles.button, ...styles.caseActionButtonSecondary }}
              onClick={async event => {
                event.stopPropagation();
                if (!casePath) return;
                const allowed = await guardTaskWorkspaceOpen(currentTask);
                if (!allowed) return;
                navigate(`/repair/${casePath}`);
              }}
              disabled={disableCaseNavigation}
            >
              Open repair task
            </button>
          </div>
        </div>
      );
    }

    if (activeGroupCode === 'DECOMMISSION') {
      return (
        <div style={styles.installPhasePanel}>
          <div style={styles.installPhaseHeader}>
            <span style={styles.installPhaseHint}>Decommission workflow</span>
            <span style={styles.installPhaseNote}>Plan removal / repack steps</span>
          </div>
          <div style={styles.installPhaseButtons}>
            <button
              type="button"
              style={{
                ...styles.installPhaseButton,
                borderColor: activeMeta.color,
                color: activeMeta.color
              }}
              onClick={async event => {
                event.stopPropagation();
                const allowed = await guardTaskWorkspaceOpen(currentTask);
                if (!allowed) return;
                handleOpenDecommission(caseItem.id);
              }}
            >
              Decommission
            </button>
          </div>
        </div>
      );
    }

    if (activeGroupCode === 'SAFETY') {
      return renderSafetyUpgradeWorkspace();
    }

    return (
      <div style={styles.assignmentPlaceholder}>
        Module workflows for {activeMeta.label.toLowerCase()} are coming soon.
      </div>
    );
  };

  const surveyResponseScoreDisplay = getCaseSurveyScoreDisplay(surveyResponseDetail);

  return (
    <div
      style={styles.caseQuickViewOverlay}
      onClick={event => event.stopPropagation()}
    >
      <div
        style={styles.caseQuickViewCard}
        onClick={event => event.stopPropagation()}
      >
        <div style={styles.caseQuickViewHeader}>
          <div style={styles.caseQuickViewTitleWrap}>
            <div style={styles.caseQuickViewTitle}>
              Case #{getCaseDisplayNumber(caseItem)}
            </div>
            <div style={styles.caseQuickViewSubTitle}>
              {openedDate}
            </div>
          </div>
          <button
            type="button"
            style={styles.caseQuickViewCloseButton}
            onClick={closeCaseQuickView}
          >
            Close
          </button>
        </div>

        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.85rem',
            backgroundColor: CASE_BRAND_PANEL,
            padding: '0.85rem 0.95rem',
            display: 'grid',
            gap: '0.65rem'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '0.55rem'
            }}
          >
            <div
              style={{
                border: `1px solid ${CASE_BRAND_BORDER}`,
                borderRadius: '0.7rem',
                backgroundColor: CASE_BRAND_CARD,
                padding: '0.58rem 0.7rem'
              }}
            >
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.73rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Customer
              </div>
              <div style={{ color: CASE_BRAND_TEXT, fontSize: '1rem', fontWeight: 700 }}>
                {(caseItem.customer as any)?.companyName ??
                  (caseItem.customer as any)?.company ??
                  (caseItem.customer as any)?.email ??
                  'Customer'}
              </div>
            </div>
            <div
              style={{
                border: `1px solid ${CASE_BRAND_BORDER}`,
                borderRadius: '0.7rem',
                backgroundColor: CASE_BRAND_CARD,
                padding: '0.58rem 0.7rem'
              }}
            >
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.73rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Device
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ color: CASE_BRAND_TEXT, fontSize: '1rem', fontWeight: 700 }}>
                  {quickViewDeviceModelLabel}
                </div>
                {activeSlaAssignment ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: '999px',
                      padding: '0.16rem 0.5rem',
                      fontSize: '0.68rem',
                      lineHeight: 1.2,
                      backgroundColor: '#E7F2EC',
                      color: '#25624D',
                      border: '1px solid #BDD4C7',
                      fontWeight: 700
                    }}
                  >
                    SLA
                  </span>
                ) : null}
                {activeWarrantyStatus.active ? (
                  <span style={getDeviceWarrantyChipStyle(activeWarrantyStatus)}>
                    {getDeviceWarrantyChipLabel(activeWarrantyStatus)}
                  </span>
                ) : null}
                {activeWarrantyStatus.isReplacementPeriod ? (
                  <span style={getDeviceWarrantyFirstMonthChipStyle()}>First month</span>
                ) : null}
              </div>
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.82rem' }}>
                {quickViewDeviceIdentifierLabel}
              </div>
            </div>
            <div
              style={{
                border: `1px solid ${CASE_BRAND_BORDER}`,
                borderRadius: '0.7rem',
                backgroundColor: CASE_BRAND_CARD,
                padding: '0.58rem 0.7rem'
              }}
            >
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.73rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Task
              </div>
              <div style={{ color: CASE_BRAND_TEXT, fontSize: '1rem', fontWeight: 700 }}>
                {hasTaskContext ? activeMeta.label : 'Unassigned'}
              </div>
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.82rem' }}>
                Task workflow
              </div>
            </div>
            <div
              style={{
                border: `1px solid ${CASE_BRAND_BORDER}`,
                borderRadius: '0.7rem',
                backgroundColor: CASE_BRAND_CARD,
                padding: '0.58rem 0.7rem'
              }}
            >
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.73rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Assignees
              </div>
              <div style={{ color: CASE_BRAND_TEXT, fontSize: '1rem', fontWeight: 700 }}>
                {taskAssigneeLabel}
              </div>
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.82rem' }}>
                {hasTaskContext ? `${formatTaskStatus(currentTask?.status)} ${activeMeta.label.toLowerCase()} task` : 'No task'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.55rem' }}>
            {activeGroupCode === 'REPAIR' && <span style={badgeStyle}>{analysisMeta.label}</span>}
            <span style={getStatusStyle(caseItem.status)}>
              {formatCaseStatusLabel ? formatCaseStatusLabel(caseItem.status) : caseItem.status}
            </span>
            <span style={getPriorityStyle(caseItem.priority)}>{formatPriorityLabel(caseItem.priority)}</span>
            <span
              style={{
                ...styles.assignmentChip,
                backgroundColor: hasTaskContext ? activeMeta.color : CASE_BRAND_TEXT_TINT,
                color: hasTaskContext ? '#ffffff' : CASE_BRAND_MUTED
              }}
            >
              {hasTaskContext ? activeMeta.label : 'No task'}
            </span>
          </div>
        </div>

        {deviceVerificationRequired ? (
          renderDeviceVerificationGate()
        ) : (
          <>
        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.85rem',
            backgroundColor: CASE_BRAND_CARD,
            padding: '0.95rem 1rem',
            display: 'grid',
            gap: '0.85rem'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '0.75rem',
              flexWrap: 'wrap',
              alignItems: 'flex-start'
            }}
          >
            <div style={{ fontSize: '1rem', fontWeight: 700, color: CASE_BRAND_TEXT }}>Schedule</div>
            <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
              <span
                style={{
                  ...styles.assignmentChip,
                  ...(hasCommittedSchedule
                    ? {
                        backgroundColor: CASE_BRAND_SUCCESS_BG,
                        border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`,
                        color: CASE_BRAND_SUCCESS_TEXT
                      }
                    : {
                        backgroundColor: CASE_BRAND_TEXT_TINT,
                        border: `1px solid ${CASE_BRAND_TEXT_TINT_BORDER}`,
                        color: CASE_BRAND_TEXT
                      })
                }}
              >
                {hasCommittedSchedule ? 'Scheduled' : 'Unscheduled'}
              </span>
              {hasScheduleRequest ? (
                <span
                  style={{
                    ...styles.assignmentChip,
                    backgroundColor: '#FDEDEC',
                    border: '1px solid rgba(166, 59, 50, 0.2)',
                    color: '#A63B32'
                  }}
                >
                  Requested
                </span>
              ) : null}
              {activeSlaAssignment ? (
                <span
                  style={{
                    ...styles.assignmentChip,
                    backgroundColor: CASE_BRAND_SUCCESS_BG,
                    border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`,
                    color: CASE_BRAND_SUCCESS_TEXT
                  }}
                >
                  SLA
                </span>
              ) : null}
              {hasTaskContext ? (
                <button
                  type="button"
                  style={{ ...styles.assignmentButton, ...styles.assignmentButtonPrimary }}
                  onClick={openScheduleRequestModal}
                  disabled={!canOpenScheduleRequest}
                >
                  {scheduleRequestSaving
                    ? 'Requesting...'
                    : hasSuggestedSchedule
                    ? 'Edit request'
                    : hasCommittedSchedule
                    ? 'Request schedule change'
                    : 'Request schedule'}
                </button>
              ) : null}
            </div>
          </div>

          {hasTaskContext ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: '0.7rem'
              }}
            >
              <div
                style={{
                  border: `1px solid ${scheduleUsesRequestedTone ? requestedScheduleTone.border : CASE_BRAND_BORDER}`,
                  borderRadius: '0.75rem',
                  backgroundColor: scheduleUsesRequestedTone ? requestedScheduleTone.background : CASE_BRAND_PANEL,
                  padding: '0.65rem 0.75rem',
                  display: 'grid',
                  gap: '0.2rem'
                }}
              >
                <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: CASE_BRAND_MUTED }}>
                  Planned Start
                </div>
                <div style={{ fontSize: '0.94rem', fontWeight: 700, color: scheduleUsesRequestedTone ? requestedScheduleTone.text : CASE_BRAND_TEXT }}>
                  {formatCaseScheduleDateTime(scheduleDisplayStartAt)}
                </div>
              </div>
              <div
                style={{
                  border: `1px solid ${scheduleUsesRequestedTone ? requestedScheduleTone.border : CASE_BRAND_BORDER}`,
                  borderRadius: '0.75rem',
                  backgroundColor: scheduleUsesRequestedTone ? requestedScheduleTone.background : CASE_BRAND_PANEL,
                  padding: '0.65rem 0.75rem',
                  display: 'grid',
                  gap: '0.2rem'
                }}
              >
                <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: CASE_BRAND_MUTED }}>
                  Planned Finish
                </div>
                <div style={{ fontSize: '0.94rem', fontWeight: 700, color: scheduleUsesRequestedTone ? requestedScheduleTone.text : CASE_BRAND_TEXT }}>
                  {formatCaseScheduleDateTime(scheduleDisplayEndAt)}
                </div>
              </div>
              <div
                style={{
                  border: `1px solid ${scheduleUsesRequestedTone ? requestedScheduleTone.border : CASE_BRAND_BORDER}`,
                  borderRadius: '0.75rem',
                  backgroundColor: scheduleUsesRequestedTone ? requestedScheduleTone.background : CASE_BRAND_PANEL,
                  padding: '0.65rem 0.75rem',
                  display: 'grid',
                  gap: '0.2rem'
                }}
              >
                <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: CASE_BRAND_MUTED }}>
                  Duration
                </div>
                <div style={{ fontSize: '0.94rem', fontWeight: 700, color: scheduleUsesRequestedTone ? requestedScheduleTone.text : CASE_BRAND_TEXT }}>
                  {scheduleDisplayDuration}
                </div>
              </div>
              {canConfirmSchedule ? (
                <button
                  type="button"
                  onClick={confirmTaskSchedule}
                  disabled={scheduleConfirmSaving}
                  style={{
                    border: `1px solid ${hasScheduleRequest ? requestedScheduleTone.border : CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    backgroundColor: hasScheduleRequest ? requestedScheduleTone.background : CASE_BRAND_PANEL,
                    padding: '0.65rem 0.75rem',
                    display: 'grid',
                    gap: '0.2rem',
                    textAlign: 'left',
                    cursor: scheduleConfirmSaving ? 'wait' : 'pointer'
                  }}
                >
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: CASE_BRAND_MUTED }}>
                    Planner Status
                  </div>
                  <div style={{ fontSize: '0.94rem', fontWeight: 700, color: CASE_BRAND_TEXT }}>
                    {scheduleConfirmSaving ? 'Confirming...' : plannerStatusLabel}
                  </div>
                </button>
              ) : (
                <div
                  style={{
                    border: `1px solid ${hasScheduleRequest ? requestedScheduleTone.border : CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    backgroundColor: hasScheduleRequest ? requestedScheduleTone.background : CASE_BRAND_PANEL,
                    padding: '0.65rem 0.75rem',
                    display: 'grid',
                    gap: '0.2rem'
                  }}
                >
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: CASE_BRAND_MUTED }}>
                    Planner Status
                  </div>
                  <div style={{ fontSize: '0.94rem', fontWeight: 700, color: CASE_BRAND_TEXT }}>
                    {plannerStatusLabel}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                padding: '0.85rem 0.95rem',
                borderRadius: '0.8rem',
                border: `1px dashed ${CASE_BRAND_BORDER}`,
                backgroundColor: CASE_BRAND_PANEL,
                color: CASE_BRAND_MUTED,
                lineHeight: 1.5
              }}
            >
              No task is currently assigned, so no schedule can be requested yet.
            </div>
          )}

          {hasTaskContext && !canSubmitScheduleRequest ? (
            <div
              style={{
                padding: '0.85rem 0.95rem',
                borderRadius: '0.8rem',
                border: `1px dashed ${CASE_BRAND_BORDER}`,
                backgroundColor: CASE_BRAND_PANEL,
                color: CASE_BRAND_MUTED,
                lineHeight: 1.5
              }}
            >
              Only the active task assignee or an ADMIN / SUPERUSER scheduler can submit a suggested visit window for planner review.
            </div>
          ) : null}

          {scheduleRequestError ? (
            <div
              style={{
                padding: '0.85rem 0.95rem',
                borderRadius: '0.8rem',
                border: '1px solid rgba(166, 59, 50, 0.2)',
                backgroundColor: '#FDEDEC',
                color: '#A63B32',
                lineHeight: 1.5
              }}
            >
              {scheduleRequestError}
            </div>
          ) : null}
        </div>

        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.85rem',
            backgroundColor: CASE_BRAND_CARD,
            padding: '0.95rem 1rem',
            display: 'grid',
            gap: '0.75rem'
          }}
        >
          <div style={styles.assignmentHeader}>
            <div style={styles.assignmentSummary}>
              <div style={styles.assignmentTitle}>
                Assigned task
              </div>
              <div style={styles.assignmentCopy}>{assignmentDescription}</div>
              {assignmentStatusParts && (
                <div style={styles.assignmentMetaRow}>{assignmentStatusParts}</div>
              )}
            </div>
            <div style={styles.assignmentBadgeStack}>
              <span
                style={{
                  ...styles.assignmentChip,
                  backgroundColor: hasTaskContext ? activeMeta.color : CASE_BRAND_TEXT_TINT,
                  color: hasTaskContext ? '#ffffff' : CASE_BRAND_MUTED
                }}
              >
                {hasTaskContext ? activeMeta.label : 'Unassigned'}
              </span>
            </div>
          </div>

          {assignmentErrorMessage && (
            <div style={styles.assignmentError}>{assignmentErrorMessage}</div>
          )}

          {renderTaskWorkspaceActions()}

          <div style={{ display: 'grid', gap: '0.4rem' }}>
            <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
              {canEditWorkflow && (
                <button
                  type="button"
                  style={{ ...styles.assignmentButton, ...styles.assignmentButtonPrimary }}
                  onClick={event => {
                    event.stopPropagation();
                    setWorkflowEditorOpen(prev => !prev);
                    if (assignmentError?.caseId === caseItem.id) {
                      setAssignmentError(null);
                    }
                  }}
                  disabled={isAssigningCurrentCase}
                >
                  {workflowButtonLabel}
                </button>
              )}
              <button
                type="button"
                style={{ ...styles.assignmentButton, ...styles.assignmentButtonGhost }}
                onClick={event => {
                  event.stopPropagation();
                  loadCaseDetails(caseItem);
                }}
              >
                View history
              </button>
            </div>
            <div style={{ fontSize: '0.78rem', color: CASE_BRAND_MUTED }}>
              Use this editor to manage task group, assignees, status, and priority together.
            </div>
          </div>
        </div>

        {scheduleRequestModalOpen && scheduleRequestDraft && hasTaskContext ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(18, 63, 67, 0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              zIndex: 1350
            }}
            onClick={event => event.stopPropagation()}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="case-schedule-request-title"
              style={{
                width: 'min(760px, 100%)',
                maxHeight: '86vh',
                overflowY: 'auto',
                borderRadius: '0.9rem',
                border: `1px solid ${CASE_BRAND_BORDER}`,
                backgroundColor: CASE_BRAND_CARD,
                padding: '1rem',
                display: 'grid',
                gap: '1rem',
                boxShadow: '0 24px 48px rgba(18, 63, 67, 0.18)'
              }}
              onClick={event => event.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'grid', gap: '0.25rem' }}>
                  <div id="case-schedule-request-title" style={{ fontSize: '1.12rem', fontWeight: 700, color: CASE_BRAND_TEXT }}>
                    {hasCommittedSchedule ? 'Request Schedule Change' : 'Request Schedule'}
                  </div>
                  <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                    <span style={{ ...styles.assignmentChip, backgroundColor: CASE_BRAND_TEXT_TINT, border: `1px solid ${CASE_BRAND_TEXT_TINT_BORDER}`, color: CASE_BRAND_TEXT }}>
                      {caseItem.caseCode || caseItem.caseNumber || 'Case'}
                    </span>
                    <span style={{ ...styles.assignmentChip, backgroundColor: CASE_BRAND_SUCCESS_BG, border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`, color: CASE_BRAND_SUCCESS_TEXT }}>
                      {taskAssigneeLabel}
                    </span>
                  </div>
                </div>
                {activeSlaAssignment ? (
                  <span
                    style={{
                      ...styles.assignmentChip,
                      backgroundColor: CASE_BRAND_SUCCESS_BG,
                      border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`,
                      color: CASE_BRAND_SUCCESS_TEXT
                    }}
                  >
                    SLA
                  </span>
                ) : null}
                {activeWarrantyStatus.active ? (
                  <span style={getDeviceWarrantyChipStyle(activeWarrantyStatus)}>
                    {getDeviceWarrantyChipLabel(activeWarrantyStatus)}
                  </span>
                ) : null}
                {activeWarrantyStatus.isReplacementPeriod ? (
                  <span style={getDeviceWarrantyFirstMonthChipStyle()}>First month</span>
                ) : null}
              </div>

              <div
                style={{
                  padding: '0.85rem 0.95rem',
                  borderRadius: '0.78rem',
                  border: `1px solid ${CASE_BRAND_BORDER}`,
                  backgroundColor: CASE_BRAND_PANEL,
                  display: 'grid',
                  gap: '0.35rem'
                }}
              >
                <div
                  style={{
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    color: CASE_BRAND_MUTED,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em'
                  }}
                >
                  Suggested Window
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: CASE_BRAND_TEXT, lineHeight: 1.4 }}>
                  {scheduleRequestStartPreview && scheduleRequestEndPreview
                    ? `${formatCaseScheduleDateTime(scheduleRequestStartPreview)} to ${formatCaseScheduleDateTime(scheduleRequestEndPreview)}`
                    : 'Not set'}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '0.9rem'
                }}
              >
                <div
                  style={{
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.78rem',
                    backgroundColor: CASE_BRAND_PANEL,
                    padding: '0.9rem',
                    display: 'grid',
                    gap: '0.75rem'
                  }}
                >
                  <div style={{ display: 'grid', gap: '0.15rem' }}>
                    <div style={{ fontSize: '0.92rem', fontWeight: 800, color: CASE_BRAND_TEXT }}>Requested Start</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                    <label style={{ display: 'grid', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 800, color: CASE_BRAND_TEXT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Date
                      </span>
                      <input
                        type="date"
                        value={scheduleRequestDraft.startDate}
                        onChange={event => updateScheduleRequestStartField('startDate', event.target.value)}
                        style={scheduleRequestInputStyle}
                      />
                    </label>
                    <label style={{ display: 'grid', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 800, color: CASE_BRAND_TEXT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Time
                      </span>
                      <input
                        type="time"
                        value={scheduleRequestDraft.startTime}
                        onChange={event => updateScheduleRequestStartField('startTime', event.target.value)}
                        style={scheduleRequestInputStyle}
                      />
                    </label>
                  </div>
                </div>

                <div
                  style={{
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.78rem',
                    backgroundColor: CASE_BRAND_PANEL,
                    padding: '0.9rem',
                    display: 'grid',
                    gap: '0.75rem'
                  }}
                >
                  <div style={{ display: 'grid', gap: '0.15rem' }}>
                    <div style={{ fontSize: '0.92rem', fontWeight: 800, color: CASE_BRAND_TEXT }}>Requested Finish</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                    <label style={{ display: 'grid', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 800, color: CASE_BRAND_TEXT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Date
                      </span>
                      <input
                        type="date"
                        value={scheduleRequestDraft.endDate}
                        min={scheduleRequestDraft.startDate || undefined}
                        onChange={event => updateScheduleRequestEndField('endDate', event.target.value)}
                        style={scheduleRequestInputStyle}
                      />
                    </label>
                    <label style={{ display: 'grid', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.78rem', fontWeight: 800, color: CASE_BRAND_TEXT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Time
                      </span>
                      <input
                        type="time"
                        value={scheduleRequestDraft.endTime}
                        onChange={event => updateScheduleRequestEndField('endTime', event.target.value)}
                        style={scheduleRequestInputStyle}
                      />
                    </label>
                  </div>
                </div>
              </div>

              {scheduleRequestDraft.validationMessage ? (
                <div
                  style={{
                    padding: '0.85rem 0.95rem',
                    borderRadius: '0.8rem',
                    border: '1px solid rgba(166, 59, 50, 0.2)',
                    backgroundColor: '#FDEDEC',
                    color: '#A63B32',
                    lineHeight: 1.5
                  }}
                >
                  {scheduleRequestDraft.validationMessage}
                </div>
              ) : null}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.65rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={{ ...scheduleRequestActionButtonStyle, ...styles.assignmentButtonGhost }}
                  onClick={closeScheduleRequestModal}
                  disabled={scheduleRequestSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  style={{ ...scheduleRequestActionButtonStyle, ...styles.assignmentButtonPrimary }}
                  onClick={saveScheduleRequest}
                  disabled={scheduleRequestSaving}
                >
                  {scheduleRequestSaving ? 'Saving...' : 'Request schedule'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {workflowEditorOpen && canEditWorkflow && (
          <div
            style={{
              border: `1px solid ${CASE_BRAND_BORDER}`,
              borderRadius: '0.85rem',
              backgroundColor: CASE_BRAND_PANEL,
              padding: '0.95rem 1rem',
              display: 'grid',
              gap: '0.9rem'
            }}
          >
            <div style={{ display: 'grid', gap: '0.25rem' }}>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: CASE_BRAND_TEXT }}>
                Workflow and assignments
              </div>
              <div style={{ fontSize: '0.8rem', color: CASE_BRAND_MUTED }}>
                Update case workflow, assignment, and task grouping in one place.
              </div>
            </div>

            <div
              style={{
                border: `1px solid ${CASE_BRAND_ACCENT_BG}`,
                backgroundColor: CASE_BRAND_ACCENT_BG,
                borderRadius: '0.75rem',
                padding: '0.65rem 0.75rem',
                display: 'grid',
                gap: '0.2rem'
              }}
            >
              <div
                style={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: CASE_BRAND_ACCENT_TEXT,
                  fontWeight: 800
                }}
              >
                Workflow controls
              </div>
              <div style={{ fontSize: '0.84rem', color: CASE_BRAND_TEXT, fontWeight: 600 }}>
                Choose task group, then status, priority, and assignees.
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: '0.7rem'
              }}
            >
              {canAssignCases && (
                <div
                  style={{
                    display: 'grid',
                    gap: '0.3rem',
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    backgroundColor: CASE_BRAND_CARD,
                    padding: '0.55rem 0.65rem'
                  }}
                >
                  <label
                    style={{
                      fontSize: '0.76rem',
                      color: CASE_BRAND_TEXT,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em'
                    }}
                  >
                    Task Group
                  </label>
                  <select
                    style={workflowSelectStyle}
                    value={workflowDraft.taskGroup || ''}
                    onChange={event => {
                      stageCaseWorkflowDraft(caseItem, {
                        taskGroup: String(event.target.value || '') as any
                      });
                    }}
                    disabled={finalActionBusy || caseBlocksOperationalActions}
                  >
                    <option value="">Select task group</option>
                    {TASK_GROUPS.map((group: any) => (
                      <option key={group.code} value={group.code}>
                        {group.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {canUpdateCaseStatus && (
                <div
                  style={{
                    display: 'grid',
                    gap: '0.3rem',
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    backgroundColor: CASE_BRAND_CARD,
                    padding: '0.55rem 0.65rem'
                  }}
                >
                  <label
                    style={{
                      fontSize: '0.76rem',
                      color: CASE_BRAND_TEXT,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em'
                    }}
                  >
                    Status
                  </label>
                  <select
                    style={workflowSelectStyle}
                    value={workflowDraft.status}
                    onChange={e =>
                      stageCaseWorkflowDraft(caseItem, {
                        status: e.target.value as any
                      })
                    }
                    disabled={statusSelectDisabled}
                  >
                    {workflowStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {canUpdateCasePriority && (
                <div
                  style={{
                    display: 'grid',
                    gap: '0.3rem',
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    backgroundColor: CASE_BRAND_CARD,
                    padding: '0.55rem 0.65rem'
                  }}
                >
                  <label
                    style={{
                      fontSize: '0.76rem',
                      color: CASE_BRAND_TEXT,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em'
                    }}
                  >
                    Priority
                  </label>
                  <select
                    style={workflowSelectStyle}
                    value={workflowDraft.priority}
                    onChange={e =>
                      stageCaseWorkflowDraft(caseItem, {
                        priority: String(e.target.value || 'MEDIUM').toUpperCase()
                      })
                    }
                    disabled={finalActionBusy || caseBlocksOperationalActions}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Standard</option>
                    <option value="HIGH">High</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
              )}
              {canAssignCases && (
                <div
                  style={{
                    display: 'grid',
                    gap: '0.3rem',
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    backgroundColor: CASE_BRAND_CARD,
                    padding: '0.55rem 0.65rem'
                  }}
                >
                  <label
                    style={{
                      fontSize: '0.76rem',
                      color: CASE_BRAND_TEXT,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em'
                    }}
                  >
                    Assignees
                  </label>
                  <select
                    style={workflowSelectStyle}
                    value=""
                    onChange={event => {
                      const candidateId = String(event.target.value || '').trim();
                      if (!candidateId) return;
                      if (selectedAssigneeIds.includes(candidateId)) {
                        event.target.value = '';
                        return;
                      }
                      stageCaseWorkflowDraft(caseItem, {
                        assigneeIds: [...selectedAssigneeIds, candidateId]
                      });
                      event.target.value = '';
                    }}
                    disabled={!assigneeUnlocked || caseAssigneesLoading || finalActionBusy || caseBlocksOperationalActions}
                  >
                    <option value="">Add assignee...</option>
                    {caseAssigneeOptions.map((option: any) => {
                      const label = option.fullName?.trim() || option.username;
                      const roleSuffix = option.roleLabel ? ` (${option.roleLabel})` : '';
                      return (
                        <option
                          key={option.id}
                          value={option.id}
                          disabled={selectedAssigneeIds.includes(option.id)}
                        >
                          {label}{roleSuffix}
                        </option>
                      );
                    })}
                  </select>
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                    {selectedAssignees.length > 0 ? (
                      selectedAssignees.map((entry: any) => (
                        <div
                          key={entry.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            border: `1px solid ${CASE_BRAND_BORDER}`,
                            backgroundColor: CASE_BRAND_PANEL,
                            borderRadius: '999px',
                            padding: '0.22rem 0.5rem',
                            fontSize: '0.72rem',
                            color: CASE_BRAND_TEXT
                          }}
                        >
                          <span>{entry.label}</span>
                          <button
                            type="button"
                            onClick={() => {
                              stageCaseWorkflowDraft(caseItem, {
                                assigneeIds: selectedAssigneeIds.filter((value: string) => value !== entry.id)
                              });
                            }}
                            disabled={finalActionBusy || caseBlocksOperationalActions}
                            style={{
                              border: 'none',
                              backgroundColor: 'transparent',
                              color: CASE_BRAND_ACCENT_TEXT,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              cursor: finalActionBusy || caseBlocksOperationalActions ? 'not-allowed' : 'pointer',
                              opacity: finalActionBusy || caseBlocksOperationalActions ? 0.65 : 1,
                              padding: 0
                            }}
                            title="Remove assignee"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: CASE_BRAND_MUTED }}>No assignees selected yet.</span>
                    )}
                  </div>
                  {caseAssigneesError && (
                    <div style={{ fontSize: '0.75rem', color: '#b91c1c' }}>
                      {caseAssigneesError}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: CASE_BRAND_MUTED }}>
                    Use the `Remove` action on each selected assignee to unassign them.
                  </div>
                  {!assigneeUnlocked && (
                    <div style={{ fontSize: '0.75rem', color: CASE_BRAND_MUTED }}>
                      Pick a task group first. Assignee selection unlocks once a task is active.
                    </div>
                  )}
                </div>
              )}
            </div>

            {canAssignCases && (
              <div style={{ fontSize: '0.78rem', color: CASE_BRAND_MUTED }}>
                Task group changes are staged here and only written when you save workflow.
              </div>
            )}

            {(canUpdateCaseStatus || canUpdateCasePriority) && (
              <div style={{ fontSize: '0.8rem', color: CASE_BRAND_MUTED }}>
                Task workflow updates can override case status.
              </div>
            )}

            {canShowFinalCaseAction && (
              <div
                style={{
                  border: `1px solid ${CASE_BRAND_ACCENT_BG}`,
                  backgroundColor: '#fff7e8',
                  borderRadius: '0.8rem',
                  padding: '0.85rem 0.95rem',
                  display: 'grid',
                  gap: '0.55rem'
                }}
              >
                <div style={{ display: 'grid', gap: '0.18rem' }}>
                  <div
                    style={{
                      fontSize: '0.76rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: CASE_BRAND_ACCENT_TEXT,
                      fontWeight: 800
                    }}
                  >
                    {canShowFinalResolveAction ? 'Resolve case' : 'Final case close'}
                  </div>
                  <div style={{ fontSize: '0.88rem', color: CASE_BRAND_TEXT, fontWeight: 600 }}>
                    {canShowFinalResolveAction
                      ? 'Use this when the closed case has been checked and is fully complete.'
                      : 'Use this only when the service team is satisfied the case is fully complete.'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: CASE_BRAND_MUTED, lineHeight: 1.5 }}>
                    {canShowFinalResolveAction
                      ? 'Resolving the case is the final lock state. A resolved case cannot be reopened.'
                      : 'Closing the case triggers the customer survey workflow. Admin and superuser status controls can reopen or resolve it later; resolved cases stay locked.'}
                  </div>
                </div>
                {workflowDirty && (
                  <div style={{ fontSize: '0.78rem', color: CASE_BRAND_ACCENT_TEXT, fontWeight: 700 }}>
                    {canShowFinalResolveAction
                      ? 'Save or cancel other workflow changes before resolving the case.'
                      : 'Save or cancel other workflow changes before closing the case.'}
                  </div>
                )}
                <div>
                  <button
                    type="button"
                    style={{
                      ...styles.quickViewPrimaryPillButton,
                      ...(finalActionBusy || workflowDirty
                        ? {
                            opacity: 0.6,
                            cursor: 'not-allowed'
                          }
                        : {
                            backgroundColor: CASE_BRAND_ACCENT_TEXT,
                            borderColor: CASE_BRAND_ACCENT_TEXT
                          })
                    }}
                    onClick={async () => {
                      const didComplete = canShowFinalResolveAction
                        ? await resolveCaseWorkflow(caseItem)
                        : await closeCaseWorkflow(caseItem);
                      if (didComplete) {
                        setWorkflowEditorOpen(false);
                      }
                    }}
                    disabled={finalActionBusy || workflowDirty}
                  >
                    {canShowFinalResolveAction
                      ? workflowResolving
                        ? 'Resolving...'
                        : 'Mark resolved'
                      : workflowClosing
                      ? 'Closing...'
                      : 'Close case'}
                  </button>
                </div>
              </div>
            )}

            {workflowError ? (
              <div style={{ fontSize: '0.8rem', color: '#b91c1c' }}>
                {workflowError}
              </div>
            ) : null}

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                style={styles.quickViewPrimaryPillButton}
                onClick={() => saveCaseWorkflow(caseItem)}
                disabled={finalActionBusy || !workflowDirty}
              >
                {workflowSaving ? 'Saving...' : 'Save workflow'}
              </button>
              <button
                type="button"
                style={styles.quickViewGhostPillButton}
                onClick={() => {
                  resetCaseWorkflowDraft(caseItem.id);
                  setWorkflowEditorOpen(false);
                }}
                disabled={finalActionBusy}
              >
                Cancel changes
              </button>
            </div>
          </div>
        )}

        {caseMedia.length > 0 ? (
          <div
            style={{
              border: `1px solid ${CASE_BRAND_BORDER}`,
              borderRadius: '0.85rem',
              backgroundColor: CASE_BRAND_CARD,
              padding: '0.95rem 1rem',
              display: 'grid',
              gap: '0.75rem'
            }}
          >
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: CASE_BRAND_TEXT }}>
                Attached notices and documents
              </div>
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
                Files supplied with this case, such as safety notices, recall documents, or work instructions.
              </div>
            </div>
            <div style={{ display: 'grid', gap: '0.55rem' }}>
              {caseMedia.map((media: any, index: number) => {
                const mediaUrl = resolveCaseMediaUrl(baseUrl, media?.fileUrl);
                const fileName = String(media?.fileName || media?.fileType || `Attachment ${index + 1}`).trim();
                const fileMeta = [
                  media?.mimeType || media?.fileType || 'File',
                  formatCaseMediaFileSize(media?.fileSize)
                ].filter(Boolean).join(' | ');
                return (
                  <div
                    key={media?.id || `${fileName}-${index}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: '0.75rem',
                      alignItems: 'center',
                      border: `1px solid ${CASE_BRAND_BORDER}`,
                      borderRadius: '0.75rem',
                      backgroundColor: CASE_BRAND_PANEL,
                      padding: '0.65rem 0.75rem'
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          color: CASE_BRAND_TEXT,
                          fontWeight: 700,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={fileName}
                      >
                        {fileName}
                      </div>
                      <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.8rem' }}>
                        {fileMeta || 'Attachment'}
                      </div>
                    </div>
                    {mediaUrl ? (
                      <button
                        type="button"
                        style={styles.quickViewUtilityButton}
                        onClick={event => {
                          event.stopPropagation();
                          setCaseAttachmentViewer({
                            ...media,
                            url: mediaUrl,
                            fileName,
                            mimeType: media?.mimeType || null,
                            fileType: media?.fileType || null,
                            fileSize: media?.fileSize || null
                          });
                        }}
                      >
                        Open
                      </button>
                    ) : (
                      <span style={{ color: CASE_BRAND_MUTED, fontSize: '0.8rem' }}>Unavailable</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        <CaseAttachmentViewerModal
          attachment={caseAttachmentViewer}
          onClose={() => setCaseAttachmentViewer(null)}
          formatFileSize={formatCaseMediaFileSize}
        />

        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.85rem',
            backgroundColor: CASE_BRAND_PANEL,
            padding: '0.95rem 1rem',
            display: 'grid',
            gap: '0.75rem'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '1rem', fontWeight: 700, color: CASE_BRAND_TEXT }}>
                Problem details
              </div>
              <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
                Review and adjust the case context for troubleshooting.
              </div>
            </div>
            {!isEditingProblem && canEditCaseProblem && (
              <button
                type="button"
                style={styles.quickViewUtilityButton}
                onClick={() => startProblemEdit(caseItem)}
              >
                Edit service details
              </button>
            )}
          </div>

          {!isEditingProblem && (
            <div style={{ display: 'grid', gap: '0.55rem' }}>
              <div style={{ color: CASE_BRAND_TEXT, fontWeight: 600 }}>
                {problemParsed.summary || 'No summary provided.'}
              </div>
              {problemParsed.details.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: CASE_BRAND_MUTED }}>
                  {problemParsed.details.map((detail: any) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
                  No additional service details captured.
                </div>
              )}
            </div>
          )}

          {isEditingProblem && (
            <div style={{ display: 'grid', gap: '0.85rem' }}>
              <div style={{ display: 'grid', gap: '0.35rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: CASE_BRAND_MUTED }}>
                  Summary
                </label>
                <textarea
                  style={{
                    ...styles.searchInput,
                    width: '100%',
                    minHeight: '96px',
                    borderRadius: '0.75rem',
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    padding: '0.65rem 0.8rem',
                    backgroundColor: CASE_BRAND_CARD
                  }}
                  value={editProblemSummary}
                  onChange={event => setEditProblemSummary(event.target.value)}
                  disabled={isSavingProblem}
                />
              </div>
              <div style={{ display: 'grid', gap: '0.45rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: CASE_BRAND_MUTED }}>
                  Additional details
                </label>
                <div style={{ display: 'grid', gap: '0.6rem' }}>
                  {editProblemDetails.map((detail: any, index: number) => (
                    <div
                      key={`detail-${index}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '0.5rem',
                        alignItems: 'center',
                        border: `1px solid ${CASE_BRAND_BORDER}`,
                        backgroundColor: CASE_BRAND_CARD,
                        borderRadius: '0.75rem',
                        padding: '0.45rem'
                      }}
                    >
                      <input
                        type="text"
                        style={{
                          ...styles.searchInput,
                          flex: 1,
                          marginBottom: 0,
                          border: 'none',
                          borderRadius: '0.55rem',
                          padding: '0.5rem 0.65rem'
                        }}
                        value={detail}
                        onChange={event => updateProblemDetail(index, event.target.value)}
                        disabled={isSavingProblem}
                      />
                      <button
                        type="button"
                        style={styles.quickViewDangerPillButton}
                        onClick={() => removeProblemDetailLine(index)}
                        disabled={isSavingProblem || editProblemDetails.length === 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  style={styles.quickViewAddDetailButton}
                  onClick={addProblemDetailLine}
                  disabled={isSavingProblem}
                >
                  + Add detail
                </button>
              </div>
              {problemEditError && (
                <div style={{ color: '#dc2626', fontSize: '0.85rem' }}>
                  {problemEditError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={styles.quickViewPrimaryPillButton}
                  onClick={() => saveProblemDetails(caseItem)}
                  disabled={isSavingProblem}
                >
                  {isSavingProblem ? 'Saving...' : 'Save details'}
                </button>
                <button
                  type="button"
                  style={styles.quickViewGhostPillButton}
                  onClick={cancelProblemEdit}
                  disabled={isSavingProblem}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.75rem',
            padding: '0.9rem 1.1rem',
            backgroundColor: CASE_BRAND_PANEL,
            marginBottom: '0.2rem'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ fontWeight: 700, color: CASE_BRAND_TEXT }}>
              Saved Quotes ({caseCardQuotes.length})
            </div>
            {caseCardQuotes.length > 0 && (
              <button
                type="button"
                style={{ ...styles.quoteViewButton, minWidth: '120px' }}
                onClick={() => {
                  setCaseQuotePreview(caseCardQuotes[0]);
                  setCaseQuotePreviewContext(caseItem);
                }}
              >
                View Latest
              </button>
            )}
          </div>
          {caseCardQuotesLoading && (
            <div style={{ marginTop: '0.45rem', color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
              Loading quotes...
            </div>
          )}
          {!caseCardQuotesLoading && caseCardQuotes.length === 0 && (
            <div style={{ marginTop: '0.45rem', color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
              No quotes saved yet.
            </div>
          )}
          {!caseCardQuotesLoading && caseCardQuotes.length > 0 && (
            <div style={{ ...styles.quoteList, marginTop: '0.6rem' }}>
              {caseCardQuotes.slice(0, 3).map((quote: any) => {
                const decisionBadge = getQuoteDecisionBadge(quote);
                const partsBadge = getQuotePartsBadge(quote);
                return (
                  <div key={`case-card-quote-${quote.quoteId}`} style={styles.quoteCard}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {quote.title}
                        <span style={decisionBadge.style}>{decisionBadge.label}</span>
                      </div>
                      <div style={styles.quoteMeta}>
                        {quote.templateName || 'Quote'} | Total $
                        {Number(quote?.totals?.grandTotal || 0).toFixed(2)}
                      </div>
                      {partsBadge && (
                        <div style={styles.quoteMeta}>
                          <span style={partsBadge.style}>{partsBadge.label}</span>
                          {partsBadge.detail ? (
                            <span style={{ marginLeft: '0.45rem' }}>{partsBadge.detail}</span>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      style={styles.quoteViewButton}
                      onClick={() => {
                        setCaseQuotePreview(quote);
                        setCaseQuotePreviewContext(caseItem);
                      }}
                    >
                      View Quote
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.75rem',
            padding: '0.9rem 1.1rem',
            backgroundColor: CASE_BRAND_PANEL
          }}
        >
          <div style={{ fontWeight: 700, color: CASE_BRAND_TEXT, marginBottom: surveyRecords.length > 0 ? '0.6rem' : 0 }}>
            Customer Survey ({surveyRecords.length})
          </div>
          {surveyRecords.length === 0 && (
            <div style={{ marginTop: '0.45rem', color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
              No survey has been released for this case yet.
            </div>
          )}
          {surveyRecords.length > 0 && (
            <div style={{ ...styles.quoteList, marginTop: '0.6rem' }}>
              {surveyRecords.slice(0, 3).map((survey: any) => {
                const surveyStatus = String(survey?.status || '').trim().toUpperCase();
                const isCompleted = surveyStatus === 'COMPLETED';
                const scoreDisplay = getCaseSurveyScoreDisplay(survey);
                const isResponseLoading = surveyResponseLoadingId === String(survey?.id || '');
                const surveyBadgeStyle = isCompleted
                  ? {
                      background: CASE_BRAND_SUCCESS_BG,
                      color: CASE_BRAND_SUCCESS_TEXT,
                      border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`
                    }
                  : {
                      background: CASE_BRAND_ACCENT_BG,
                      color: CASE_BRAND_ACCENT_TEXT,
                      border: `1px solid ${CASE_BRAND_TEXT_TINT_BORDER}`
                    };
                return (
                  <div key={`case-card-survey-${survey.id}`} style={styles.quoteCard}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {survey.surveyName || 'Customer Satisfaction Survey'}
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            borderRadius: '999px',
                            padding: '0.15rem 0.5rem',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            marginLeft: '0.5rem',
                            ...surveyBadgeStyle
                          }}
                        >
                          {isCompleted ? 'Completed' : 'Waiting'}
                        </span>
                      </div>
                      <div style={styles.quoteMeta}>
                        {isCompleted
                          ? `Submitted ${survey.completedAt ? new Date(survey.completedAt).toLocaleString() : 'recently'}`
                          : `Released ${survey.releasedAt ? new Date(survey.releasedAt).toLocaleString() : 'recently'}`}
                      </div>
                      <div style={styles.quoteMeta}>
                        {survey.technicianName || 'Support team'} | {survey.questionCount || 0} question
                        {Number(survey.questionCount || 0) === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gap: '0.45rem',
                        justifyItems: 'end',
                        alignContent: 'center',
                        minWidth: '180px'
                      }}
                    >
                      {isCompleted && scoreDisplay ? (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.45rem',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            color: CASE_BRAND_TEXT,
                            fontWeight: 800
                          }}
                        >
                          <span>Score {scoreDisplay.scoreLabel}</span>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              borderRadius: '999px',
                              border: '1px solid transparent',
                              padding: '0.12rem 0.5rem',
                              fontSize: '0.74rem',
                              fontWeight: 800,
                              ...scoreDisplay.tone
                            }}
                          >
                            {scoreDisplay.percentageLabel}
                          </span>
                        </div>
                      ) : (
                        <div style={{ ...styles.quoteMeta, fontWeight: 700, color: CASE_BRAND_TEXT }}>
                          Awaiting customer feedback
                        </div>
                      )}
                      {isCompleted && canViewSurveyResponseDetails ? (
                        <button
                          type="button"
                          style={{
                            ...styles.assignmentButton,
                            ...styles.assignmentButtonGhost,
                            minHeight: '2rem',
                            padding: '0.35rem 0.7rem',
                            fontSize: '0.82rem'
                          }}
                          onClick={event => openSurveyResponseDetail(event, survey)}
                          disabled={Boolean(surveyResponseLoadingId)}
                        >
                          {isResponseLoading ? 'Loading...' : 'View responses'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {surveyResponseError ? (
                <div
                  style={{
                    border: '1px solid rgba(166, 59, 50, 0.2)',
                    backgroundColor: '#FDEDEC',
                    color: '#A63B32',
                    borderRadius: '0.65rem',
                    padding: '0.65rem 0.75rem',
                    fontSize: '0.85rem'
                  }}
                >
                  {surveyResponseError}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {activeSlaAssignment ? (
          <div
            style={{
              border: `1px solid ${CASE_BRAND_BORDER}`,
              borderRadius: '0.75rem',
              padding: '0.9rem 1.1rem',
              backgroundColor: CASE_BRAND_PANEL,
              marginBottom: '0.2rem'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: CASE_BRAND_TEXT }}>
                  SLA Agreement
                </div>
                <div style={{ marginTop: '0.35rem', color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
                  {activeSlaLabel} | {activeSlaPeriod}
                </div>
              </div>
              {activeSlaHasSavedReport ? (
                <button
                  type="button"
                  style={{ ...styles.quoteViewButton, minWidth: '140px' }}
                  onClick={() => setSlaAgreementViewerOpen(true)}
                >
                  View Agreement
                </button>
              ) : null}
            </div>
            {!activeSlaHasSavedReport ? (
              <div style={{ marginTop: '0.6rem', color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
                This device has an active SLA assignment, but no saved customer-facing agreement has
                been published yet.
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          style={{
            border: `1px solid ${CASE_BRAND_BORDER}`,
            borderRadius: '0.75rem',
            padding: '0.9rem 1.1rem',
            backgroundColor: CASE_BRAND_PANEL
          }}
        >
          <div style={{ fontWeight: 700, color: CASE_BRAND_TEXT }}>
            Service Reports ({serviceReports.length})
          </div>
          {serviceReports.length === 0 && (
            <div style={{ marginTop: '0.45rem', color: CASE_BRAND_MUTED, fontSize: '0.85rem' }}>
              No service reports saved yet.
            </div>
          )}
          {serviceReports.length > 0 && (
            <div style={{ ...styles.quoteList, marginTop: '0.6rem' }}>
              {serviceReports.slice(0, 3).map((report: any) => {
                const reportStatus = String(report?.status || '').trim().toUpperCase();
                const statusStyle =
                  reportStatus === 'SENT'
                    ? {
                        background: CASE_BRAND_SUCCESS_BG,
                        color: CASE_BRAND_SUCCESS_TEXT,
                        border: `1px solid ${CASE_BRAND_SUCCESS_BORDER}`
                      }
                    : {
                        background: CASE_BRAND_TEXT_TINT,
                        color: CASE_BRAND_TEXT,
                        border: `1px solid ${CASE_BRAND_TEXT_TINT_BORDER}`
                      };
                const isOpening = caseServiceReportPreviewLoadingId === report.id;
                return (
                  <div key={`case-card-report-${report.id}`} style={styles.quoteCard}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>
                        {report.title || 'Service Report'}
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            borderRadius: '999px',
                            padding: '0.15rem 0.5rem',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            marginLeft: '0.5rem',
                            ...statusStyle
                          }}
                        >
                          {reportStatus === 'SENT' ? 'Sent' : 'Saved'}
                        </span>
                      </div>
                      <div style={styles.quoteMeta}>
                        {report.templateName || 'Service report'}
                        {report.savedAt
                          ? ` | ${new Date(report.savedAt).toLocaleString()}`
                          : ''}
                      </div>
                      {report.savedByName && (
                        <div style={styles.quoteMeta}>{report.savedByName}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      style={styles.quoteViewButton}
                      onClick={() => openCaseServiceReportPreview(report, caseItem)}
                      disabled={isOpening}
                    >
                      {isOpening ? 'Opening...' : 'View Report'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {surveyResponseDetail ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(17, 24, 39, 0.48)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              zIndex: 2050
            }}
            onClick={event => event.stopPropagation()}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="case-survey-responses-title"
              style={{
                width: 'min(980px, 100%)',
                maxHeight: '88vh',
                borderRadius: '1rem',
                backgroundColor: CASE_BRAND_CARD,
                border: `1px solid ${CASE_BRAND_BORDER}`,
                boxShadow: '0 28px 60px rgba(15, 23, 42, 0.24)',
                display: 'grid',
                gridTemplateRows: 'auto 1fr'
              }}
              onClick={event => event.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  padding: '1.1rem 1.25rem',
                  borderBottom: `1px solid ${CASE_BRAND_BORDER}`
                }}
              >
                <div style={{ display: 'grid', gap: '0.25rem' }}>
                  <div id="case-survey-responses-title" style={{ color: CASE_BRAND_TEXT, fontSize: '1.45rem', fontWeight: 800 }}>
                    Survey responses
                  </div>
                  <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.92rem' }}>
                    {surveyResponseDetail.surveyName || 'Customer Satisfaction Survey'}
                  </div>
                </div>
                <button
                  type="button"
                  style={styles.caseQuickViewCloseButton}
                  onClick={closeSurveyResponseDetail}
                >
                  Close
                </button>
              </div>

              <div
                style={{
                  overflowY: 'auto',
                  padding: '1.1rem 1.25rem 1.25rem',
                  display: 'grid',
                  gap: '1rem'
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '0.75rem'
                  }}
                >
                  <div style={{ border: `1px solid ${CASE_BRAND_BORDER}`, borderRadius: '0.75rem', padding: '0.8rem', background: CASE_BRAND_PANEL }}>
                    <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Score
                    </div>
                    {surveyResponseScoreDisplay ? (
                      <div style={{ marginTop: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ color: CASE_BRAND_TEXT, fontWeight: 800 }}>
                          {surveyResponseScoreDisplay.scoreLabel}
                        </span>
                        <span
                          style={{
                            display: 'inline-flex',
                            borderRadius: '999px',
                            border: '1px solid transparent',
                            padding: '0.15rem 0.55rem',
                            fontSize: '0.78rem',
                            fontWeight: 800,
                            ...surveyResponseScoreDisplay.tone
                          }}
                        >
                          {surveyResponseScoreDisplay.percentageLabel}
                        </span>
                      </div>
                    ) : (
                      <div style={{ marginTop: '0.35rem', color: CASE_BRAND_TEXT, fontWeight: 700 }}>Not scored</div>
                    )}
                  </div>
                  <div style={{ border: `1px solid ${CASE_BRAND_BORDER}`, borderRadius: '0.75rem', padding: '0.8rem', background: CASE_BRAND_PANEL }}>
                    <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Submitted
                    </div>
                    <div style={{ marginTop: '0.35rem', color: CASE_BRAND_TEXT, fontWeight: 700 }}>
                      {surveyResponseDetail.completedAt
                        ? formatCaseScheduleDateTime(parseCaseScheduleDate(surveyResponseDetail.completedAt))
                        : 'Not recorded'}
                    </div>
                  </div>
                  <div style={{ border: `1px solid ${CASE_BRAND_BORDER}`, borderRadius: '0.75rem', padding: '0.8rem', background: CASE_BRAND_PANEL }}>
                    <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Contact
                    </div>
                    <div style={{ marginTop: '0.35rem', color: CASE_BRAND_TEXT, fontWeight: 700 }}>
                      {surveyResponseDetail.submittedByContactName || surveyResponseDetail.recipientContactName || 'Customer contact'}
                    </div>
                    {surveyResponseDetail.submittedByContactEmail ? (
                      <div style={{ marginTop: '0.15rem', color: CASE_BRAND_MUTED, fontSize: '0.82rem' }}>
                        {surveyResponseDetail.submittedByContactEmail}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ border: `1px solid ${CASE_BRAND_BORDER}`, borderRadius: '0.75rem', padding: '0.8rem', background: CASE_BRAND_PANEL }}>
                    <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Technician
                    </div>
                    <div style={{ marginTop: '0.35rem', color: CASE_BRAND_TEXT, fontWeight: 700 }}>
                      {surveyResponseDetail.technicianName || 'Support team'}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '0.7rem' }}>
                  {(Array.isArray(surveyResponseDetail.sections) ? surveyResponseDetail.sections : []).map((section, index) => {
                    const rawScore =
                      section.rawScore === null || section.rawScore === undefined ? NaN : Number(section.rawScore);
                    const maxScore =
                      section.maxScore === null || section.maxScore === undefined ? NaN : Number(section.maxScore);
                    const hasScore = Number.isFinite(rawScore);
                    const hasMax = Number.isFinite(maxScore) && maxScore > 0;
                    const scoreLabel = hasScore
                      ? `${rawScore}${hasMax ? `/${maxScore}` : ''}${String(section.scaleType || '').toUpperCase().startsWith('STARS_') ? ' stars' : ''}`
                      : 'Not answered';
                    return (
                      <div
                        key={section.questionKey || `survey-response-${index}`}
                        style={{
                          border: `1px solid ${CASE_BRAND_BORDER}`,
                          borderRadius: '0.75rem',
                          background: '#ffffff',
                          padding: '0.85rem 0.95rem',
                          display: 'grid',
                          gap: '0.5rem'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.85rem', flexWrap: 'wrap' }}>
                          <div style={{ color: CASE_BRAND_TEXT, fontWeight: 800 }}>
                            {section.title || `Question ${index + 1}`}
                          </div>
                          <span
                            style={{
                              ...styles.assignmentChip,
                              backgroundColor: CASE_BRAND_TEXT_TINT,
                              border: `1px solid ${CASE_BRAND_TEXT_TINT_BORDER}`,
                              color: CASE_BRAND_TEXT
                            }}
                          >
                            {scoreLabel}
                          </span>
                        </div>
                        {section.description ? (
                          <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.88rem', lineHeight: 1.5 }}>
                            {section.description}
                          </div>
                        ) : null}
                        <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.8rem' }}>
                          {getCaseSurveyScaleLabel(section)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    border: `1px solid ${CASE_BRAND_BORDER}`,
                    borderRadius: '0.75rem',
                    background: CASE_BRAND_PANEL,
                    padding: '0.9rem 1rem',
                    display: 'grid',
                    gap: '0.45rem'
                  }}
                >
                  <div style={{ color: CASE_BRAND_TEXT, fontWeight: 800 }}>
                    {surveyResponseDetail.notesPrompt || 'Customer comments'}
                  </div>
                  <div style={{ color: CASE_BRAND_MUTED, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {String(surveyResponseDetail.commentsText || '').trim() || 'No customer comments provided.'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {slaAgreementViewerOpen ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(17, 24, 39, 0.48)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              zIndex: 2000
            }}
            onClick={event => event.stopPropagation()}
          >
            <div
              style={{
                width: 'min(1080px, 100%)',
                maxHeight: '88vh',
                borderRadius: '1rem',
                backgroundColor: '#fffdf9',
                border: `1px solid ${CASE_BRAND_BORDER}`,
                boxShadow: '0 28px 60px rgba(15, 23, 42, 0.24)',
                display: 'grid',
                gridTemplateRows: 'auto 1fr'
              }}
              onClick={event => event.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1.1rem 1.25rem',
                  borderBottom: `1px solid ${CASE_BRAND_BORDER}`
                }}
              >
                <div style={{ display: 'grid', gap: '0.2rem' }}>
                  <div style={{ color: CASE_BRAND_TEXT, fontSize: '1.5rem', fontWeight: 800 }}>
                    {deviceSlaReport?.title || activeSlaLabel}
                  </div>
                  {deviceSlaReport?.subtitle ? (
                    <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.95rem' }}>
                      {deviceSlaReport.subtitle}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  style={styles.caseQuickViewCloseButton}
                  onClick={() => setSlaAgreementViewerOpen(false)}
                >
                  Close
                </button>
              </div>
              <div
                style={{
                  overflowY: 'auto',
                  padding: '1.1rem 1.25rem 1.25rem'
                }}
              >
                {deviceSlaReportLoading ? (
                  <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.9rem' }}>
                    Loading the saved customer agreement...
                  </div>
                ) : deviceSlaReportError ? (
                  <div style={{ color: '#b91c1c', fontSize: '0.9rem' }}>{deviceSlaReportError}</div>
                ) : deviceSlaReport ? (
                  <CustomerFacingSlaReportView report={deviceSlaReport} />
                ) : (
                  <div style={{ color: CASE_BRAND_MUTED, fontSize: '0.9rem' }}>
                    No saved customer-facing agreement is available for this SLA.
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
          </>
        )}
      </div>
    </div>
  );
});
